import { gql, useSubscription } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Dialog } from '@headlessui/react';
import { Auth } from 'aws-amplify';
import download from 'downloadjs';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import images from 'src/common/images';
import {
  ContractPeriod,
  DataUser,
  FilEstimaDetail,
  MeisaiTableRowProps,
  MitsumoriInput,
  MstHinInfo,
  MstHinmei,
  MstHinmeiFree,
  MstOffice,
  MstRemarks,
  PrimaryStore,
  ReportData,
  ReportDetail,
  ReportHeader,
  SecondaryStore,
  TotalPriceTableProps,
} from 'src/common/interfaces';
import { DiscountType, MitsumoriKubun, MitsumoriType } from 'src/common/number-enum';
import { Delimiter, FieldLength, RecordState } from 'src/common/string-enum';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { deleteEstimaData, registerEstimaData } from 'src/graphql/mutations';
import {
  getEspRegisterNo,
  getFilEstimaDetail,
  getFilEstimaPoint,
  getLogMaintenance,
  getMstContractPeriod,
  getMstHinInfo,
  getMstHinmei,
  getMstNetRate,
  getMstOffice,
  getMstPrimarySales,
  getMstProduct,
  getMstRemarks,
  getMstSecondarySales,
  getMstUser,
} from 'src/graphql/queries';
import { insertedLogMaintenance } from 'src/graphql/subscriptions';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import { pathURL, rowTableColor } from '../../common/constants';
import ModalGuideContractPeriod from '../guide-contract-period/guide-contract-period';
import ModalGuideHinmei from '../guide-hinmei/guide-hinmei';
import ModalGuideOffice from '../guide-office/guide-office';
import ModalGuidePrimaryStore from '../guide-primary-store/guide-primary-store';
import ModalGuideProduct, { MprProduct } from '../guide-product/guide-product';
import ModalGuideRemarks from '../guide-remarks/guide-remarks';
import ModalGuideSecondaryStore from '../guide-secondary-store/guide-secondary-store';
import ModalGuideUser from '../guide-user/guide-user';
import MainLayout from '../main-layout';
import Maintenance from '../maintenance';
import { MstUser } from '../navigation-bar/navigation-bar';
import DatetimePicker from './datetime-picker';
import EstimaInput from './estima-input';
import RangeInput from './range-input';
import RowMeisaiTable from './row-meisai-table';
import Selection, { Option } from './selection';
type QuotLine = {
  [key in keyof FilEstimaDetail]?: {
    type: 'text' | 'number';
    autoPreFix?: boolean;
    formatCurrency?: boolean;
    maxLength?: number;
  };
};

type ButtonListType = {
  label: string;
  onClick: () => void;
  className: string;
};

export interface MeisaiRowProps extends FilEstimaDetail {
  isReadOnly?: boolean;
  isFreeInput?: boolean;
  rowInputSettings?: QuotLine;
  esd_hin_cd: number;
  esd_net_rate?: number;
  __typename?: string;
}

export interface TableHeader {
  title: string;
  data: string;
  className?: string;
}

const hinmeiCdInputFree = 900000000;

const newOrAddOptions = [
  { value: MitsumoriKubun.REGISTER, label: '新規' },
  { value: MitsumoriKubun.UPDATE, label: '追加' },
];

const settingProductOptions = [
  { value: 0, label: 'アラカルト' },
  { value: 1, label: 'セットモデル' },
];

const infoTableHeaders: TableHeader[] = [
  { title: '', data: 'label' },
  { title: '標準金額', data: 'basePrice' },
  { title: '仕切金額', data: 'detailPrice' },
  { title: '保守金額', data: 'maintainPrice' },
];

const meisaiIchiranHeader: TableHeader[] = [
  { title: '名称', data: 'esd_hin_nm', className: 'w-[30%]' },
  { title: '数量', data: 'esd_suryo' },
  { title: '標準金額', data: 'esd_standard_price' },
  { title: '仕切率', data: 'esd_net_rate' },
  { title: '仕切金額', data: 'esd_net_price' },
  { title: '保守金額', data: 'esd_maintenance_price' },
  { title: '仕切率変更理由 ※', data: 'esd_net_reason' },
  { title: '削除', data: '' },
];

const baseRowInputSettings: QuotLine = {
  esd_hin_nm: {
    type: 'text',
  },
  esd_suryo: {
    type: 'number',
    autoPreFix: false,
  },
  esd_standard_price: {
    type: 'text',
    formatCurrency: true,
    maxLength: 11,
  },
  esd_net_rate: {
    autoPreFix: false,
    type: 'number',
  },
  esd_net_price: {
    type: 'text',
    formatCurrency: true,
    maxLength: 11,
  },
  esd_maintenance_price: {
    type: 'text',
    formatCurrency: true,
    maxLength: 11,
  },
  esd_net_reason: {
    type: 'text',
  },
};

const emptyRow: MeisaiRowProps = {
  isFreeName: 1,
  isFreeInput: true,
  esd_hin_nm: '',
  esd_net_reason: '',
  esd_suryo: 0,
  esd_hin_cd: hinmeiCdInputFree,
  rowInputSettings: baseRowInputSettings,
};

type ErrorProps = {
  esp_soft_mainte_price: boolean;
  esp_remaining_month: boolean;
  esp_product_cd: boolean;
  esp_new_or_add_kb: boolean;
  esp_secondary_nm: boolean;
  esp_est_date: boolean;
  esp_est_nm: boolean;
  esp_customer_nm: boolean;
  esp_primary_nm: boolean;
  mps_primary_nm: boolean;
  mss_secondary_nm: boolean;
  esp_print_format: boolean;
  esp_sh_jyoken: boolean;
  esp_ex_date_st: boolean;
  esp_es_deli_date_st: boolean;
  esp_mainte_cont: boolean;
  mpr_product_nm: boolean;
  esp_set_model: boolean;
  esp_primary_cd: boolean;
  esp_secondary_cd: boolean;
  esp_net_reason: boolean;
  esd_net_reason: boolean;
  esp_sum_mainte_price: boolean;
};

const defaultError: ErrorProps = {
  esp_est_date: false,
  esp_est_nm: false,
  esp_customer_nm: false,
  esp_primary_nm: false,
  mps_primary_nm: false,
  mss_secondary_nm: false,
  esp_print_format: false,
  esp_sh_jyoken: false,
  esp_ex_date_st: false,
  esp_es_deli_date_st: false,
  esp_mainte_cont: false,
  mpr_product_nm: false,
  esp_set_model: false,
  esp_remaining_month: false,
  esp_product_cd: false,
  esp_new_or_add_kb: false,
  esp_secondary_nm: false,
  esp_soft_mainte_price: false,
  esp_primary_cd: false,
  esp_secondary_cd: false,
  esp_net_reason: false,
  esd_net_reason: false,
  esp_sum_mainte_price: false,
};

type DiscountProps = {
  software: number;
  template: number;
  license: number;
  useware: number;
  kdssMaintenance: number;
};

// type DiscountTableType = {
//   name: string;
//   type: MitsumoriType;
// };

const discountDefault: DiscountProps = {
  software: 100,
  template: 100,
  license: 100,
  useware: 100,
  kdssMaintenance: 100,
};

// const discountTableHeaders = {
//   title: '値引率',
//   data: [
//     { name: 'software', type: MitsumoriType.SOFTWARE },
//     { name: 'template', type: MitsumoriType.TEMPLATE },
//     { name: 'license', type: MitsumoriType.LICENSE },
//     { name: 'useware', type: MitsumoriType.USEWARE },
//     { name: 'kdssMaintenance', type: MitsumoriType.KDSS },
//   ],
//   className: '!w-[34px]',
// };

const rowInfoTables: TotalPriceTableProps[] = [
  {
    label: 'ソフトウェア',
    basePrice: 0,
    detailPrice: 0,
    maintainPrice: 0,
    kdssBasePrice: 0,
    kdssDetailPrice: 0,
    type: MitsumoriType.SOFTWARE,
    rowInputSettings: {
      basePrice: { formatCurrency: true },
      detailPrice: { formatCurrency: true },
      maintainPrice: { formatCurrency: true },
      kdssBasePrice: { formatCurrency: true },
      kdssDetailPrice: { formatCurrency: true },
    },
  },
  {
    label: 'テンプレート',
    basePrice: 0,
    detailPrice: 0,
    maintainPrice: 0,
    kdssBasePrice: 0,
    kdssDetailPrice: 0,
    type: MitsumoriType.TEMPLATE,
    rowInputSettings: {
      basePrice: { formatCurrency: true },
      detailPrice: { formatCurrency: true },
      maintainPrice: { formatCurrency: true },
      kdssBasePrice: { formatCurrency: true },
      kdssDetailPrice: { formatCurrency: true },
    },
  },
  {
    label: 'ライセンス',
    basePrice: 0,
    detailPrice: 0,
    maintainPrice: 0,
    kdssBasePrice: 0,
    kdssDetailPrice: 0,
    type: MitsumoriType.LICENSE,
    rowInputSettings: {
      basePrice: { formatCurrency: true },
      detailPrice: { formatCurrency: true },
      maintainPrice: { formatCurrency: true },
      kdssBasePrice: { formatCurrency: true },
      kdssDetailPrice: { formatCurrency: true },
    },
  },
  {
    label: 'ユースウェア',
    basePrice: 0,
    detailPrice: 0,
    type: MitsumoriType.USEWARE,
    rowInputSettings: {
      basePrice: { formatCurrency: true },
      detailPrice: { formatCurrency: true },
    },
  },
  {
    label: 'KDSS保守',
    basePrice: 0,
    detailPrice: 0,
    type: MitsumoriType.KDSS,
    rowInputSettings: {
      basePrice: { formatCurrency: true },
      detailPrice: { formatCurrency: true },
    },
  },
  {
    label: '合計金額',
    basePrice: 0,
    detailPrice: 0,
    rowInputSettings: {
      basePrice: { formatCurrency: true },
      detailPrice: { formatCurrency: true },
    },
  },
];

const emptyMitsumoriInput: MitsumoriInput = {
  mcp_usage_fee_discount_rate: 0,
  mpr_discount_type: 0,
  esp_contract_period: 0,
  esp_customer_nm: '',
  esp_divide_maintenance: 0,
  esp_es_deli_date_st: '',
  esp_est_date: '',
  esp_est_kb_no: 0,
  esp_est_nm: '',
  esp_estima_no: 0,
  esp_ex_date_st: '',
  esp_license_num: '',
  esp_mainte_cont: '',
  esp_memo1: '',
  esp_memo2: '',
  esp_memo3: '',
  esp_municipalities: '',
  esp_net_reason: '',
  esp_new_or_add_kb: 0,
  esp_office_cd: 0,
  esp_prefectures: '',
  esp_primary_cd: undefined,
  esp_primary_nm: '',
  esp_primary_rep: '',
  esp_print_format: '',
  esp_product_cd: undefined,
  esp_remaining_month: 0,
  esp_secondary_cd: undefined,
  esp_secondary_nm: '',
  esp_secondary_rep: '',
  esp_set_model: undefined,
  esp_sh_jyoken: '',
  esp_soft_mainte_price: 0,
  esp_syain_cd: 0,
  esp_template_net_rate: 100,
  esp_useware_net_rate: 100,
  esp_software_net_rate: 100,
  esp_license_net_rate: 100,
  esp_maintenance_net_rate: 100,
  mss_secondary_nm: '',
  status: '',
  mpr_product_nm: '',
  mps_primary_nm: '',
  mo_office_nm: '',
  mpr_maintenance_limit: 0,
  mcp_maintenance_discount_rate: 100,
  mnr_useware_net_rate: 100,
  mnr_license_net_rate: 100,
  mnr_template_net_rate: 100,
  mnr_software_net_rate: 100,
  mnr_maintenance_net_rate: 100,
  esp_software_discount_rate: 0,
  esp_template_discount_rate: 0,
  esp_license_discount_rate: 0,
  esp_useware_discount_rate: 0,
  esp_maintenance_discount_rate: 0,
  esp_sum_mainte_price: 0,
};

const emptyHinInfo: MstHinInfo = {
  mhi_hin_cd: 0,
  mhi_primary_cd: 0,
  mhi_secondary_cd: 0,
  mhi_product_cd: 0,
  mhi_maintenance_price: 0,
  mhi_add_hinmei1: undefined,
  mhi_add_hinmei2: undefined,
  mhi_oso_cd: undefined,
  mh_maintenance_tani: 0,
  mhi_product_price: 0,
  mhi_available_kb: 0,
  mhi_years: 0,
  mhi_default_suryo: 0,
  esd_hin_nm: '',
  esd_maintenance_price: 0,
  esd_product_price: 0,
  esd_net_rate: 0,
  esd_hin_cd: 0,
  esd_net_reason: '',
  esd_hin_kb: 0,
  esd_net_price: 0,
  esd_standard_price: 0,
  esd_suryo: 0,
  isFreeInput: true,
};

type LocationProps = {
  state: {
    no: number;
    kb_no: number;
  };
};

const SOFTWARE = '＜ソフトウェア＞';
const TEMPLATE = '＜テンプレート＞';
const LICENSE = '＜ライセンス＞';
const USEWARE = '＜ユースウェア＞';
const KDSS = '＜KDSS保守＞';

const SUB_TOTAL = '＜ 小 計 ＞';
const TOTAL = '＜ 年　間　利　用　料　＞';

const QuotationInput = () => {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const location = useLocation() as unknown as LocationProps;
  const navigate = useNavigate();

  const [registerEstima] = useAppMutation({
    query: gql(registerEstimaData),
  });

  const [deleteEstimaFunc] = useAppMutation({
    query: gql(deleteEstimaData),
  });

  const { data: subInsLogData } = useSubscription(gql(insertedLogMaintenance));

  const [searchMstProduct] = useAppLazyQuery({ query: gql(getMstProduct) });
  const [searchLogMaintenance, { data: logMData }] = useAppLazyQuery({ query: gql(getLogMaintenance) });
  const [searchMstUser, { loading: isUserLoading }] = useAppLazyQuery({ query: gql(getMstUser) });
  const [searchContractPeriod] = useAppLazyQuery({ query: gql(getMstContractPeriod) });
  const [searchMstPrimarySales] = useAppLazyQuery({ query: gql(getMstPrimarySales) });
  const [searchMstSecondarySales] = useAppLazyQuery({ query: gql(getMstSecondarySales) });
  const [fetchFilEstimaPoint] = useAppLazyQuery({ query: gql(getFilEstimaPoint) });
  const [searchMstOffice] = useAppLazyQuery({ query: gql(getMstOffice) });
  const [fetchEspRegisterNo] = useAppLazyQuery({ query: gql(getEspRegisterNo) });
  const [fetchMstHinInfo] = useAppLazyQuery({ query: gql(getMstHinInfo) });
  const [fetchMstHinInfoFull] = useAppLazyQuery({ query: gql(getMstHinInfo) });
  const [fetchFilEstimaDetail] = useAppLazyQuery({ query: gql(getFilEstimaDetail) });
  const [fetchMstNetRate] = useAppLazyQuery({ query: gql(getMstNetRate) });
  const [searchMstRemarks] = useAppLazyQuery({ query: gql(getMstRemarks) });
  const [fetchMstHinmei, { data: mstHinmeiData }] = useAppLazyQuery({ query: gql(getMstHinmei) });

  const [hinmeiKbSelected, setHinmeiKbSelected] = useState<MitsumoriType>(MitsumoriType.SOFTWARE);
  const [isShowModaGuideHinmei, setIsShowModaGuideHinmei] = useState(false);
  const [isShowGuideContractPeriod, setIsShowGuideContractPeriod] = useState(false);
  const [mitsumoriInput, setMitsumoriInput] = useState<MitsumoriInput>(emptyMitsumoriInput);
  const [discountInput, setDiscountInput] = useState<DiscountProps>(discountDefault);
  const [isShowGuideOffice, setIsShowGuideOffice] = useState<boolean>(false);
  const [isShowGuideUser, setIsShowGuideUser] = useState<boolean>(false);
  const [isShowPrimaryStore, setIsShowPrimaryStore] = useState<boolean>(false);
  const [isShowSecondaryStore, setIsShowSecondaryStore] = useState<boolean>(false);
  const [isOpenProductDivison, setIsOpenProductDivison] = useState<boolean>(false);
  const [isShowModalRemarks, setIsShowModalRemarks] = useState<boolean>(false);
  const [mstRemarksKb, setMstRemarksKb] = useState<string>('');
  const [printOptions, setPrintOptions] = useState<Array<Option>>([{ label: '', value: '' }]);
  const [meisaiTableRows, setMeisaiTableRows] = useState<MeisaiTableRowProps>({
    softwareTableRows: [],
    templateTableRows: [],
    licenseTableRows: [],
    usewareTableRows: [],
    discountTableRows: [],
    kdssTableRows: [],
  });
  const [totalPriceTableData, setTotalPriceTableData] = useState<TotalPriceTableProps[]>(rowInfoTables);
  const [errors, setError] = useState<ErrorProps>(defaultError);
  const [isProcessing, setIsProcessing] = useState(false);

  const [selectedRow, setSelectedRow] = useState<{ [key in MitsumoriType]?: number | null }>({
    [MitsumoriType.SOFTWARE]: null,
  });

  const totalArrayByKey = (arr: FilEstimaDetail[], key: string, discountRow = false) => {
    if (!discountRow && arr.length <= 1) return 0;

    return arr.reduce((prev: number, curr: FilEstimaDetail) => {
      if (curr.esd_suryo !== 0) {
        return (curr[key as keyof FilEstimaDetail] ? Number(curr[key as keyof FilEstimaDetail]) : 0) + prev;
      } else {
        return prev;
      }
    }, 0);
  };

  const calcPrice = (totlaPrice: number) => Math.floor(totlaPrice);

  const calcDataTablePrice = () => {
    const rowData: TotalPriceTableProps[] = [...totalPriceTableData];

    // 各種明細の積上金額を取得
    const totalSoftwareStandardPrice = totalArrayByKey(meisaiTableRows.softwareTableRows, 'esd_standard_price');
    const totalSoftwareNetPrice = totalArrayByKey(meisaiTableRows.softwareTableRows, 'esd_net_price');
    const totalSoftwareMaintainPrice = totalArrayByKey(meisaiTableRows.softwareTableRows, 'esd_maintenance_price');
    const totalTemplateStandardPrice = totalArrayByKey(meisaiTableRows.templateTableRows, 'esd_standard_price');
    const totalTemplateNetPrice = totalArrayByKey(meisaiTableRows.templateTableRows, 'esd_net_price');
    const totalTemplateMaintainPrice = totalArrayByKey(meisaiTableRows.templateTableRows, 'esd_maintenance_price');
    const totalLicenseStandardPrice = totalArrayByKey(meisaiTableRows.licenseTableRows, 'esd_standard_price');
    const totalLicenseNetPrice = totalArrayByKey(meisaiTableRows.licenseTableRows, 'esd_net_price');
    const totalLicenseMaintainPrice = totalArrayByKey(meisaiTableRows.licenseTableRows, 'esd_maintenance_price');
    const totalUsewareStandardPrice = totalArrayByKey(meisaiTableRows.usewareTableRows, 'esd_standard_price');
    const totalUsewareNetPrice = totalArrayByKey(meisaiTableRows.usewareTableRows, 'esd_net_price');
    const totalDiscontStandardPrice = totalArrayByKey(meisaiTableRows.discountTableRows, 'esd_standard_price', true);
    const totalDiscontNetPrice = totalArrayByKey(meisaiTableRows.discountTableRows, 'esd_net_price', true);

    if (!mitsumoriInput.mpr_discount_type || mitsumoriInput.mpr_discount_type === DiscountType.USING_FEE) {
      // 一括割引対象 2:利用料

      // ソフトウェア金額
      rowData[0].basePrice = calcPrice(totalSoftwareStandardPrice);
      rowData[0].detailPrice = calcPrice(totalSoftwareNetPrice);
      rowData[0].maintainPrice = 0;

      // テンプレート金額
      rowData[1].basePrice = calcPrice(totalTemplateStandardPrice);
      rowData[1].detailPrice = calcPrice(totalTemplateNetPrice);
      rowData[1].maintainPrice = 0;

      // ライセンス金額
      rowData[2].basePrice = calcPrice(totalLicenseStandardPrice);
      rowData[2].detailPrice = calcPrice(totalLicenseNetPrice);
      rowData[2].maintainPrice = 0;

      // ユースウェア金額
      rowData[3].basePrice = calcPrice(totalUsewareStandardPrice);
      rowData[3].detailPrice = calcPrice(totalUsewareNetPrice);

      //KDSS
      rowData[4].basePrice = 0;
      rowData[4].detailPrice = 0;
    } else {
      // 一括割引対象 1:保守料

      // ソフトウェア金額
      rowData[0].basePrice = calcPrice(totalSoftwareStandardPrice);
      rowData[0].detailPrice = calcPrice(totalSoftwareNetPrice);

      // テンプレート金額
      rowData[1].basePrice = calcPrice(totalTemplateStandardPrice);
      rowData[1].detailPrice = calcPrice(totalTemplateNetPrice);

      // ライセンス金額
      rowData[2].basePrice = calcPrice(totalLicenseStandardPrice);
      rowData[2].detailPrice = calcPrice(totalLicenseNetPrice);

      // ユースウェア金額
      rowData[3].basePrice = calcPrice(totalUsewareStandardPrice);
      rowData[3].detailPrice = calcPrice(totalUsewareNetPrice);

      // ＫＤＳＳ保守金額（単年）
      // ソフトウェア保守についてはソフトウェア保守上限があるので金額を見て判断する
      if (
        totalSoftwareMaintainPrice < mitsumoriInput.mpr_maintenance_limit - mitsumoriInput.esp_soft_mainte_price ??
        0
      ) {
        // 保守上限未達のため、通常通り保守料金を設定
        rowData[0].maintainPrice = totalSoftwareMaintainPrice;
      } else if (mitsumoriInput.esp_soft_mainte_price >= mitsumoriInput.mpr_maintenance_limit) {
        // 既に上限に達しているため、０円を設定
        rowData[0].maintainPrice = 0;
      } else {
        // 今回で保守上限のため、上限値までの差額を設定
        rowData[0].maintainPrice = mitsumoriInput.mpr_maintenance_limit - mitsumoriInput.esp_soft_mainte_price ?? 0;
      }

      // テンプレート保守
      rowData[1].maintainPrice = totalTemplateMaintainPrice;
      // ライセンス保守
      rowData[2].maintainPrice = totalLicenseMaintainPrice;

      // ＫＤＳＳ保守金額（複数年や値引加味）
      if (mitsumoriInput.esp_new_or_add_kb === MitsumoriKubun.REGISTER) {
        // 標準金額
        const kdssBaseCalc = (price: number) => {
          let res = Math.floor(
            price *
              mitsumoriInput.esp_contract_period *
              (mitsumoriInput.mcp_maintenance_discount_rate / 100) *
              (discountInput.kdssMaintenance / 100)
          );
          if (mitsumoriInput.esp_contract_period >= 3) {
            // 複数年契約の場合に保守料金は1000単位にする
            res = Math.floor(res / 1000) * 1000;
          }

          return res;
        };
        rowData[0].kdssBasePrice = kdssBaseCalc(rowData[0].maintainPrice);
        rowData[1].kdssBasePrice = kdssBaseCalc(rowData[1].maintainPrice);
        rowData[2].kdssBasePrice = kdssBaseCalc(rowData[2].maintainPrice);
      } else {
        // 標準金額
        const kdssBaseCalc = (price: number) => {
          console.log('price', price);
          console.log('mitsumoriInput.esp_remaining_month', mitsumoriInput.esp_remaining_month);
          console.log('discountInput.kdssMaintenance', discountInput.kdssMaintenance);
          console.log(
            'Math.round((price * (mitsumoriInput.esp_remaining_month / 12) * (discountInput.kdssMaintenance / 100)) / 100)',
            Math.round(
              (price * (mitsumoriInput.esp_remaining_month / 12) * (discountInput.kdssMaintenance / 100)) / 100
            )
          );
          return (
            Math.round(
              (price * (mitsumoriInput.esp_remaining_month / 12) * (discountInput.kdssMaintenance / 100)) / 100
            ) * 100
          );
        };
        rowData[0].kdssBasePrice = kdssBaseCalc(rowData[0].maintainPrice);
        rowData[1].kdssBasePrice = kdssBaseCalc(rowData[1].maintainPrice);
        rowData[2].kdssBasePrice = kdssBaseCalc(rowData[2].maintainPrice);
      }

      // 仕切金額
      const kdssDetailCalc = (price: number) => {
        return Math.floor(price * (mitsumoriInput.esp_maintenance_net_rate / 100));
      };
      rowData[0].kdssDetailPrice = kdssDetailCalc(rowData[0].kdssBasePrice);
      rowData[1].kdssDetailPrice = kdssDetailCalc(rowData[1].kdssBasePrice);
      rowData[2].kdssDetailPrice = kdssDetailCalc(rowData[2].kdssBasePrice);

      // KDSS保守金額
      rowData[4].basePrice = rowData[0].kdssBasePrice + rowData[1].kdssBasePrice + rowData[2].kdssBasePrice;
      rowData[4].detailPrice = rowData[0].kdssDetailPrice + rowData[1].kdssDetailPrice + rowData[2].kdssDetailPrice;
    }

    // 合計金額
    const lastItem: TotalPriceTableProps = rowData.pop() as TotalPriceTableProps;
    const totalAllStandardPrice =
      (rowData[0].basePrice ?? 0) +
      (rowData[1].basePrice ?? 0) +
      (rowData[2].basePrice ?? 0) +
      (rowData[3].basePrice ?? 0) +
      (rowData[4].basePrice ?? 0);
    const totalAllNetPrice =
      (rowData[0].detailPrice ?? 0) +
      (rowData[1].detailPrice ?? 0) +
      (rowData[2].detailPrice ?? 0) +
      (rowData[3].detailPrice ?? 0) +
      (rowData[4].detailPrice ?? 0);
    rowData.push(lastItem);

    rowData[5].basePrice = totalDiscontStandardPrice + totalAllStandardPrice;
    rowData[5].detailPrice = totalDiscontNetPrice + totalAllNetPrice;

    setTotalPriceTableData(rowData);
  };

  const validator = () => {
    let isSuccess = true;
    const validateError: ErrorProps = { ...errors };
    let message = '';

    let mitsumoriRequired = [
      'esp_est_date',
      'esp_est_nm',
      'esp_customer_nm',
      'esp_primary_cd',
      'esp_primary_nm',
      'esp_primary_cd',
      'esp_secondary_cd',
      'esp_secondary_nm',
      'esp_sh_jyoken',
      'esp_ex_date_st',
      'esp_es_deli_date_st',
      'esp_set_model',
      'esp_product_cd',
      'esp_print_format',
    ];
    if (mitsumoriInput.mpr_discount_type === DiscountType.MAINTAIN_PRICE) {
      // 製品が保守契約の場合は以下も必須
      mitsumoriRequired = mitsumoriRequired.concat(['esp_mainte_cont']);
    }
    if (mitsumoriInput.esp_new_or_add_kb === MitsumoriKubun.UPDATE) {
      // 追加見積の場合は以下も必須
      mitsumoriRequired = mitsumoriRequired.concat(['esp_remaining_month', 'esp_soft_mainte_price']);
    }

    if (
      mitsumoriInput.esp_software_net_rate !== mitsumoriInput.mnr_software_net_rate ||
      mitsumoriInput.esp_template_net_rate !== mitsumoriInput.mnr_template_net_rate ||
      mitsumoriInput.esp_license_net_rate !== mitsumoriInput.mnr_license_net_rate ||
      mitsumoriInput.esp_useware_net_rate !== mitsumoriInput.mnr_useware_net_rate ||
      mitsumoriInput.esp_maintenance_net_rate !== mitsumoriInput.mnr_maintenance_net_rate
    ) {
      mitsumoriRequired = mitsumoriRequired.concat(['esp_net_reason']);
    }

    mitsumoriRequired.forEach((key: string) => {
      if (
        mitsumoriInput[key as keyof MitsumoriInput]?.toString() !== '0' &&
        !mitsumoriInput[key as keyof MitsumoriInput]
      ) {
        validateError[key as keyof ErrorProps] = true;
        isSuccess = false;
        if (!message) {
          message = InfoMessage.CHECK_REQUIRED;
        }
      }
    });

    const arrayField = ['softwareTableRows', 'templateTableRows', 'licenseTableRows', 'usewareTableRows'];

    const netRate = [
      mitsumoriInput.esp_software_net_rate,
      mitsumoriInput.esp_template_net_rate,
      mitsumoriInput.esp_license_net_rate,
      mitsumoriInput.esp_useware_net_rate,
    ];

    const mitsumoriType = [
      MitsumoriType.SOFTWARE,
      MitsumoriType.TEMPLATE,
      MitsumoriType.LICENSE,
      MitsumoriType.USEWARE,
    ];

    mitsumoriType.map((item: MitsumoriType, index: number) => {
      if (
        meisaiTableRows[arrayField[index] as keyof MeisaiTableRowProps].some(
          (row: FilEstimaDetail) => row.esd_net_rate && row.esd_net_rate !== netRate[index] && row.esd_net_reason === ''
        )
      ) {
        validateError['esd_net_reason'] = true;
        isSuccess = false;
        message = InfoMessage.CHECK_REQUIRED;
      }
      return isSuccess;
    });

    if (message) {
      utility.showAlertMessage('', message, InfoWord.ERROR, InfoWord.YES);
    }
    setError(validateError);

    return isSuccess;
  };

  const handleReMitsumori = () => {
    if (mitsumoriInput.status === RecordState.INSERT) {
      utility.showAlertMessage('', InfoMessage.NOT_RE_QUOTE, InfoWord.WARNING, InfoWord.YES);
      return;
    }

    utility.confirmAlert(InfoMessage.CONFIRM_QUOTE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () => {
      fetchFilEstimaPoint({ variables: { input: { esp_estima_no: mitsumoriInput.esp_estima_no } } }).then((res) => {
        const resData = res.data.getFilEstimaPoint;
        // 枝番で降順に並び替えて、それに+1を行う。
        resData.sort((a: MitsumoriInput, b: MitsumoriInput) => b.esp_est_kb_no - a.esp_est_kb_no);
        const kbNo = Number(resData[0].esp_est_kb_no) + 1;
        // 99を超えた場合は処理を止める。ケースとして少ないので、特にメッセージも用意しない
        if (kbNo >= 100) return;
        setMitsumoriInput({
          ...mitsumoriInput,
          esp_est_kb_no: kbNo,
          status: RecordState.INSERT,
        });
      });
    });
  };

  const handleDelete = () => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        if (mitsumoriInput.status === RecordState.INSERT) {
          utility.showAlertMessage('', InfoMessage.CAN_NOT_DELETE_QUOTE, InfoWord.WARNING, InfoWord.YES);
        } else {
          utility.confirmAlert(
            InfoMessage.CONFIRM_REMOVE,
            '',
            InfoWord.WARNING,
            InfoWord.YES,
            InfoWord.NO,
            sendDeleteRequest
          );
        }
      })
      .catch((err) => {
        signOut();
        navigate('/login', { replace: true, state: { from: location } });
        console.log(err);
      });
  };

  const sendDeleteRequest = () => {
    deleteEstimaFunc({
      variables: {
        input: {
          esp_est_kb_no: mitsumoriInput.esp_est_kb_no,
          esp_estima_no: mitsumoriInput.esp_estima_no,
        },
      },
    })
      .then(() => {
        navigate(pathURL.QUOTATION_LIST.pathName, { replace: true, state: { from: location } });
      })
      .catch((err) => {
        utility.showAlertMessage('', ErrorMessage.DELETE_FAILED, InfoWord.ERROR, InfoWord.OK);
        console.log(err);
      });
  };

  const inputList = [
    {
      label: '見積No.',
      onChange: () => {},
      type: 'range-custom',
      className: '!w-5/12 ',
      disabledTo: true,
      disabledFrom: true,
      inputClass: '!h-[56px]',
      from: mitsumoriInput.esp_estima_no,
      to: mitsumoriInput.esp_est_kb_no,
      autoPreFix: true,
    },
    {
      label: '営業担当者',
      value: mitsumoriInput.mu_user_name,
      onClick: () => setIsShowGuideUser(true),
      type: 'select-guide',
      className: '!w-5/12',
      fieldlength: FieldLength.MIDDLE,
    },
    {
      type: 'button-group',
      className: 'w-2/12 text-white mt-2',
      buttonList: [
        { label: '再見積', onClick: handleReMitsumori, className: 'p-4 bg-[blue] rounded-[3px] ml-auto mt-4' },
        { label: '見積削除', onClick: handleDelete, className: 'p-4 bg-[red] rounded-[3px] ml-2 mt-4' },
      ],
    },
    {
      label: '見積日付 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_est_date: value });
        setError({ ...errors, esp_est_date: false });
      },
      type: 'date',
      className: '!w-5/12',
      value: mitsumoriInput.esp_est_date,
      error: errors.esp_est_date,
    },
    {
      label: '拠点',
      onClick: () => setIsShowGuideOffice(true),
      type: 'select-guide',
      className: '!w-5/12',
      value: mitsumoriInput.mo_office_nm,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '見積名称 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_est_nm: value });
        setError({ ...errors, esp_est_nm: false });
      },
      type: 'input',
      value: mitsumoriInput.esp_est_nm,
      maxLength: 50,
      error: errors.esp_est_nm,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '都道府県',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_prefectures: value });
      },
      type: 'input',
      value: mitsumoriInput.esp_prefectures,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '市区町村',
      onChange: (value: string) => handleChangeMitsumoriInput({ esp_municipalities: value }),
      type: 'input',
      value: mitsumoriInput.esp_municipalities,
      fieldlength: FieldLength.LONG,
    },
    {
      label: 'お客様名 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_customer_nm: value });
        setError({ ...errors, esp_customer_nm: false });
      },
      type: 'input',
      className: 'mb-10',
      value: mitsumoriInput.esp_customer_nm,
      error: errors.esp_customer_nm,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '製品区分 ＊',
      onClick: () => setIsOpenProductDivison(true),
      type: 'select-guide',
      className: '!w-5/12',
      value: mitsumoriInput.mpr_product_nm,
      error: errors.esp_product_cd,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '製品構成 ＊',
      onChange: (select: Option) => {
        handleChangeMitsumoriInput({ esp_set_model: select.value });
        setError({ ...errors, esp_set_model: false });
      },
      type: 'select',
      className: '!w-5/12',
      value: utility.mapOption(mitsumoriInput.esp_set_model, settingProductOptions),
      options: settingProductOptions,
      error: errors.esp_set_model,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '見積区分',
      onChange: (select: Option) => {
        handleChangeMitsumoriInput({ esp_new_or_add_kb: select.value });
        setError({ ...errors, esp_new_or_add_kb: false });
      },
      type: 'select',
      options: newOrAddOptions,
      value: utility.mapOption(mitsumoriInput.esp_new_or_add_kb, newOrAddOptions),
      error: errors.esp_new_or_add_kb,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '一次店 ＊',
      onClick: () => {
        setIsShowPrimaryStore(true);
        setError({ ...errors, esp_primary_cd: false, esp_primary_nm: false });
      },
      className: '!w-5/12',
      type: 'select-guide',
      value: mitsumoriInput.mps_primary_nm,
      error: errors.esp_primary_cd,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '名称 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_primary_nm: value });
        setError({ ...errors, esp_primary_nm: false });
      },
      type: 'input',
      value: mitsumoriInput.esp_primary_nm,
      error: errors.esp_primary_nm,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '担当者',
      onChange: (value: string) => handleChangeMitsumoriInput({ esp_primary_rep: value }),
      type: 'input',
      value: mitsumoriInput.esp_primary_rep,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '二次店 ＊',
      onClick: () => {
        if (!mitsumoriInput.mps_primary_nm) {
          utility.showAlertMessage('', InfoMessage.REQUIRE_SELECT_PRIMARY_STORE, InfoWord.WARNING, InfoWord.YES);
          return;
        }
        setIsShowSecondaryStore(true);
        setError({ ...errors, esp_secondary_cd: false, esp_secondary_nm: false });
      },
      type: 'select-guide',
      value: mitsumoriInput.mss_secondary_nm,
      error: errors.esp_secondary_cd,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '名称 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_secondary_nm: value });
        setError({ ...errors, esp_secondary_nm: false });
      },
      type: 'input',
      value: mitsumoriInput.esp_secondary_nm,
      error: errors.esp_secondary_nm,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '担当者',
      onChange: (value: string) => handleChangeMitsumoriInput({ esp_secondary_rep: value }),
      type: 'input',
      value: mitsumoriInput.esp_secondary_rep,
      fieldlength: FieldLength.LONG,
    },
    {
      label: 'ｿﾌﾄ仕切率(%)',
      onChangeFrom: (data: number) => {
        handleChangeMitsumoriInput({ esp_software_net_rate: data });
        setError({ ...errors, esp_net_reason: false });
      },
      type: 'range-custom',
      className: '!w-4/12',
      inputClass: '!w-[70px] !h-[56px] text-center',
      disabledTo: true,
      to: mitsumoriInput.mnr_software_net_rate ?? 0,
      from: mitsumoriInput.esp_software_net_rate ?? 0,
      delimiter: Delimiter.ARROW,
    },
    {
      label: 'TP仕切率(%)',
      onChangeFrom: (data: number) => {
        handleChangeMitsumoriInput({ esp_template_net_rate: data });
        setError({ ...errors, esp_net_reason: false });
      },
      type: 'range-custom',
      className: '!w-4/12',
      inputClass: '!w-[70px] !h-[56px] text-center',
      disabledTo: true,
      to: mitsumoriInput.mnr_template_net_rate ?? 0,
      from: mitsumoriInput.esp_template_net_rate,
      delimiter: Delimiter.ARROW,
    },
    {
      label: 'ﾗｲｾﾝｽ仕切率(%)',
      onChangeFrom: (data: number) => {
        handleChangeMitsumoriInput({ esp_license_net_rate: data });
        setError({ ...errors, esp_net_reason: false });
      },
      type: 'range-custom',
      className: '!w-4/12 flex-auto',
      inputClass: '!w-[70px] !h-[56px] text-center',
      disabledTo: true,
      to: mitsumoriInput.mnr_license_net_rate ?? 0,
      from: mitsumoriInput.esp_license_net_rate,
      delimiter: Delimiter.ARROW,
    },
    {
      label: 'ﾕｰｽ仕切率(%)',
      onChangeFrom: (data: number) => {
        handleChangeMitsumoriInput({ esp_useware_net_rate: data });
        setError({ ...errors, esp_net_reason: false });
      },
      type: 'range-custom',
      className: '!w-4/12',
      inputClass: '!w-[70px] !h-[56px] text-center',
      disabledTo: true,
      to: mitsumoriInput.mnr_useware_net_rate ?? 0,
      from: mitsumoriInput.esp_useware_net_rate,
      delimiter: Delimiter.ARROW,
    },
    {
      label: '保守仕切率(%)',
      onChangeFrom: (data: number) => {
        handleChangeMitsumoriInput({ esp_maintenance_net_rate: data });
        setError({ ...errors, esp_net_reason: false });
      },
      type: 'range-custom',
      className: '!w-4/12',
      inputClass: '!w-[70px] !h-[56px] text-center',
      disabledTo: true,
      to: mitsumoriInput.mnr_maintenance_net_rate ?? 0,
      from: mitsumoriInput.esp_maintenance_net_rate,
      delimiter: Delimiter.ARROW,
    },
    {
      label: '変更理由 ※',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_net_reason: value });
        setError({ ...errors, esp_net_reason: false });
      },
      type: 'input',
      className: 'mb-10',
      value: mitsumoriInput.esp_net_reason,
      error: errors.esp_net_reason,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '印刷書式 ＊',
      onChange: (select: Option) => {
        handleChangeMitsumoriInput({ esp_print_format: select.value });
        setError({ ...errors, esp_print_format: false });
      },
      type: 'select',
      value: utility.mapOption(mitsumoriInput.esp_print_format, printOptions),
      options: printOptions,
      error: errors.esp_print_format,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '支払条件 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_sh_jyoken: value });
        setError({ ...errors, esp_sh_jyoken: false });
      },
      onClick: () => {
        setIsShowModalRemarks(true);
        setMstRemarksKb('1');
      },
      type: 'custom-guide',
      value: mitsumoriInput.esp_sh_jyoken,
      error: errors.esp_sh_jyoken,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '有効期限 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_ex_date_st: value });
        setError({ ...errors, esp_ex_date_st: false });
      },
      onClick: () => {
        setIsShowModalRemarks(true);
        setMstRemarksKb('2');
      },
      type: 'custom-guide',
      value: mitsumoriInput.esp_ex_date_st,
      error: errors.esp_ex_date_st,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '納入予定日 ＊',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_es_deli_date_st: value });
        setError({ ...errors, esp_es_deli_date_st: false });
      },
      onClick: () => {
        setIsShowModalRemarks(true);
        setMstRemarksKb('3');
      },
      type: 'custom-guide',
      value: mitsumoriInput.esp_es_deli_date_st,
      error: errors.esp_es_deli_date_st,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '保守契約条件 ※',
      onChange: (value: string) => {
        handleChangeMitsumoriInput({ esp_mainte_cont: value });
        setError({ ...errors, esp_mainte_cont: false });
      },
      onClick: () => {
        setIsShowModalRemarks(true);
        setMstRemarksKb('4');
      },
      type: 'custom-guide',
      value: mitsumoriInput.esp_mainte_cont,
      error: errors.esp_mainte_cont,
      fieldlength: FieldLength.LONG,
    },
    {
      label: 'ライセンス数',
      onChange: (value: string) => handleChangeMitsumoriInput({ esp_license_num: value }),
      type: 'number',
      value: mitsumoriInput.esp_license_num,
      autoPreFix: false,
      fieldlength: FieldLength.SHORT,
    },
    {
      label: '備考１',
      onChange: (value: string) => handleChangeMitsumoriInput({ esp_memo1: value }),
      type: 'input',
      value: mitsumoriInput.esp_memo1,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '備考２',
      onChange: (value: string) => handleChangeMitsumoriInput({ esp_memo2: value }),
      type: 'input',
      value: mitsumoriInput.esp_memo2,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '備考３',
      onChange: (value: string) => handleChangeMitsumoriInput({ esp_memo3: value }),
      type: 'input',
      className: 'mb-10',
      value: mitsumoriInput.esp_memo3,
      fieldlength: FieldLength.LONG,
    },
    {
      label: '契約期間',
      onClick: () => setIsShowGuideContractPeriod(true),
      type: 'select-guide',
      disabled: mitsumoriInput.esp_new_or_add_kb !== MitsumoriKubun.REGISTER,
      value: mitsumoriInput.esp_contract_period,
      fieldlength: FieldLength.SHORT,
    },
    {
      label: '残契約月数',
      onChange: (value: number) => handleChangeMitsumoriInput({ esp_remaining_month: value }),
      type: 'input',
      className: '!w-4/12',
      disabled: mitsumoriInput.esp_new_or_add_kb !== MitsumoriKubun.UPDATE,
      value: mitsumoriInput.esp_remaining_month,
      isMumeric: true,
      error: errors.esp_remaining_month,
      fieldlength: FieldLength.SHORT,
    },
    {
      label: '現在ｿﾌﾄ保守',
      type: 'input',
      onChange: (value: number) => handleChangeMitsumoriInput({ esp_soft_mainte_price: value }),
      className: '!w-4/12',
      disabled: mitsumoriInput.esp_new_or_add_kb !== MitsumoriKubun.UPDATE,
      value: mitsumoriInput.esp_soft_mainte_price,
      error: errors.esp_soft_mainte_price,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '現在合計保守',
      type: 'input',
      onChange: (value: number) => handleChangeMitsumoriInput({ esp_sum_mainte_price: value }),
      className: '!w-4/12',
      disabled: mitsumoriInput.esp_new_or_add_kb !== MitsumoriKubun.UPDATE,
      value: mitsumoriInput.esp_sum_mainte_price,
      error: errors.esp_sum_mainte_price,
      fieldlength: FieldLength.MIDDLE,
    },
    {
      label: '保守単位で明細を分ける',
      type: 'checkbox',
      checked: Boolean(mitsumoriInput.esp_divide_maintenance),
      onChange: () =>
        handleChangeMitsumoriInput({ esp_divide_maintenance: Number(!mitsumoriInput.esp_divide_maintenance) }),
    },
  ];

  const handleChangeMitsumoriInput = (data: Partial<MitsumoriInput>) => {
    const newMitsumoriInput = { ...mitsumoriInput, ...data };

    if (mitsumoriInput.status === RecordState.NONE) {
      newMitsumoriInput.status = RecordState.UPDATE;
    }
    setMitsumoriInput({ ...newMitsumoriInput });
  };

  const handleDownload = async (reportData: ReportData, rowCount: number) => {
    try {
      const url = `${process.env.REACT_APP_API_GW_END_POINT}/${process.env.NODE_ENV}/report-quotation`;
      const jwtToken = (await Auth.currentSession()).getAccessToken().getJwtToken();
      const pdfName = `${mitsumoriInput.esp_estima_no}-${
        mitsumoriInput.esp_est_kb_no < 10 ? '0' + mitsumoriInput.esp_est_kb_no : mitsumoriInput.esp_est_kb_no
      }.pdf`;

      // 出力ファイルを取得
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: jwtToken as string },
        body: JSON.stringify({
          template: mitsumoriInput.esp_print_format,
          rowCount, // the count of detail lines
          data: reportData,
        }),
      });

      if (res.status === 200) {
        // LocalにDownloadする
        const data = await res.blob();
        download(data, pdfName, 'application/pdf');
      } else {
        console.log('Timeout. Try again');
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleExportPdf = async () => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        if (mitsumoriInput?.status === RecordState.INSERT) {
          utility.showAlertMessage('', '', InfoWord.ERROR, InfoWord.OK, InfoMessage.ONLY_REGISTERED_DATA_EXPORT);
          return;
        }
      })
      .catch((err) => {
        signOut();
        navigate('/login', { replace: true, state: { from: location } });
        console.log(err);
        return;
      });

    setIsProcessing(true);
    await handleCalcKdssDetail();
    const [newMeisaiTable, rowCount] = constructOutputData();
    handleDownload(newMeisaiTable, rowCount).finally(() => {
      setIsProcessing(false);
    });
  };

  const constructOutputData = (): [ReportData, number] => {
    const address = (mitsumoriInput.mo_address ?? '').split(' ', 2);

    const newPoint: ReportHeader = {
      '**見積番号': `${mitsumoriInput.esp_estima_no}-${mitsumoriInput.esp_est_kb_no.toString().padStart(2, '0')}`,
      '**見積親番': mitsumoriInput.esp_estima_no,
      '**見積枝番': mitsumoriInput.esp_est_kb_no,
      '**見積日付': moment(mitsumoriInput.esp_est_date).format('YYYY年MM月DD日'),
      '**見積名称': mitsumoriInput.esp_est_nm,
      '**都道府県': mitsumoriInput.esp_prefectures,
      '**市区町村': mitsumoriInput.esp_municipalities,
      '**顧客名称': mitsumoriInput.esp_customer_nm,
      '**一次店名称': mitsumoriInput.esp_primary_nm,
      '**一次店担当者名称': mitsumoriInput.esp_primary_rep,
      '**二次店名称': mitsumoriInput.esp_secondary_nm,
      '**二次店担当者名称': mitsumoriInput.esp_secondary_rep,
      '**支払条件': mitsumoriInput.esp_sh_jyoken,
      '**有効期限': mitsumoriInput.esp_ex_date_st,
      '**納入予定日': mitsumoriInput.esp_es_deli_date_st,
      '**保守契約条件': mitsumoriInput.esp_mainte_cont,
      '**ライセンス数': mitsumoriInput.esp_license_num,
      '**備考１': mitsumoriInput.esp_memo1,
      '**備考２': mitsumoriInput.esp_memo2,
      '**備考３': mitsumoriInput.esp_memo3,
      '**契約期間': mitsumoriInput.esp_contract_period,
      '**拠点名称': mitsumoriInput.mo_office_nm ?? '',
      '**拠点郵便番号': mitsumoriInput.mo_post,
      '**拠点住所１': address[0] ?? '',
      '**拠点住所２': address[1] ?? '',
      '**拠点電話番号': mitsumoriInput.mo_tel,
      '**拠点FAX番号': mitsumoriInput.mo_fax,
      '**営業担当者名称': mitsumoriInput.mu_user_name,
      '**営業担当者メールアドレス': mitsumoriInput.mu_mail,
      '**仕切金額合計': totalPriceTableData[5].detailPrice,
      '**標準金額合計': totalPriceTableData[5].basePrice,
      '**製品区分コード': mitsumoriInput.esp_product_cd,
    };

    type ExportModel = {
      index?: string;
      esd_hin_nm?: string;
      esd_suryo?: string;
      tani?: string;
      esd_net_price?: string;
      esd_net_rate?: string;
      esd_net_reason?: string;
      esd_standard_price?: string;
      esd_product_price?: string;
      esd_maintenance_price?: string;
      mhi_oso_cd?: string;
    };

    const objectDetailKey: ExportModel = {
      index: '**明細-行番号-',
      esd_hin_nm: '**明細-名称-',
      esd_suryo: '**明細-数量-',
      esd_net_price: '**明細-仕切金額-',
      esd_net_rate: '**明細-仕切率-',
      esd_net_reason: '**明細-備考①-',
      esd_standard_price: '**明細-標準金額-',
      esd_product_price: '**明細-標準単価-',
      esd_maintenance_price: '**明細-保守金額-',
      mhi_oso_cd: '**明細-OSOコード-',
    };

    const changeValueBySuryo = (suryo: number, value: any) => {
      if (suryo) {
        return value;
      } else {
        return '';
      }
    };

    let maintenancePrice = 0;
    const formatDetailData = (rows: FilEstimaDetail[]): ReportDetail[] =>
      rows.map((item, index) => {
        const titles = [SOFTWARE, TEMPLATE, LICENSE, USEWARE, KDSS];
        const isHeading = titles.includes(item.esd_hin_nm);
        const isFooting = SUB_TOTAL === item.esd_hin_nm || TOTAL === item.esd_hin_nm;
        if (isHeading) {
          // ヘッダーの印字
          return Object.keys(objectDetailKey).reduce((prev: ExportModel, currKey: string) => {
            let value: string | number = '';

            if (currKey === 'index') value = index + 1;
            if (currKey === 'esd_hin_nm') value = item[currKey as keyof FilEstimaDetail] ?? '';

            return {
              ...prev,
              [`${objectDetailKey[currKey as keyof ExportModel] as keyof ExportModel}${index + 1}`]: value,
            };
          }, {});
        } else if (isFooting) {
          // フッターの印字
          return Object.keys(objectDetailKey).reduce((prev: ExportModel, currKey: string) => {
            let value: string | number = '';

            switch (currKey) {
              case 'index':
                value = index + 1;
                break;
              case 'esd_hin_nm':
                value =
                  (SUB_TOTAL === item.esd_hin_nm ? '　　　　　　　' : '') + item[currKey as keyof FilEstimaDetail] ??
                  '';
                break;
              case 'esd_standard_price':
              case 'esd_net_price':
                value = item[currKey as keyof FilEstimaDetail] ?? '';
                break;
              case 'esd_maintenance_price':
                value =
                  item.esd_hin_kb === MitsumoriType.USEWARE || item.esd_hin_kb === MitsumoriType.KDSS
                    ? ''
                    : (item[currKey as keyof FilEstimaDetail] ?? '');
                break;
            }

            if (item.esd_hin_kb === MitsumoriType.SOFTWARE) {
              const limitMaitain = mitsumoriInput.mpr_maintenance_limit ?? 0;
              if (maintenancePrice > limitMaitain) {
                switch (currKey) {
                  case 'mhi_oso_cd':
                    value = `上限${limitMaitain.toLocaleString()}適用`;
                    break;
                  case 'esd_maintenance_price':
                    value = limitMaitain ?? '';
                    break;
                }
              }
            }

            return {
              ...prev,
              [`${objectDetailKey[currKey as keyof ExportModel] as keyof ExportModel}${index + 1}`]: value,
            };
          }, {});
        } else {
          // 明細の印字
          const newRow = Object.keys(objectDetailKey).reduce((prev: ExportModel, currKey: string) => {
            let value: string | number;

            switch (currKey) {
              case 'index':
                value = index + 1;
                break;
              case 'esd_hin_nm':
                value = `${item.mhi_add_hinmei1 ?? ''} ${item.esd_hin_nm} ${item.mhi_add_hinmei2 ?? ''}`;
                break;
              case 'mhi_oso_cd':
                // 商品単価がマスター単価そのままであればＯＳＯコードを出力する。
                // 商品単価がマスター単価でないケース：追加で契約期間が半年、キャンペーン値引がある
                value = item.esd_product_price === item.mhi_product_price ? (item.mhi_oso_cd ?? '') : '';
                break;
              case 'esd_net_rate':
                value = changeValueBySuryo(item.esd_suryo ?? 0, item.esd_net_rate ? item.esd_net_rate / 100 : '');
                break;
              case 'esd_product_price':
                if (item.esd_hin_cd === hinmeiCdInputFree) {
                  value = changeValueBySuryo(
                    item.esd_suryo ?? 0,
                    Math.floor((item.esd_standard_price ?? 0) / (item.esd_suryo ?? 0))
                  );
                } else {
                  value = changeValueBySuryo(item.esd_suryo ?? 0, item[currKey as keyof FilEstimaDetail] ?? '');
                }
                break;
              case 'esd_maintenance_price':
                value = changeValueBySuryo(
                  item.esd_suryo ?? 0,
                  item.esd_hin_kb === MitsumoriType.USEWARE || item.esd_hin_kb === MitsumoriType.KDSS
                    ? ''
                    : (item.esd_maintenance_price ?? '')
                );
                if (item.esd_hin_kb === MitsumoriType.SOFTWARE) {
                  maintenancePrice += item.esd_maintenance_price ?? 0;
                }

                break;
              default:
                value = changeValueBySuryo(item.esd_suryo ?? 0, item[currKey as keyof FilEstimaDetail] ?? '');
                break;
            }

            return {
              ...prev,
              [`${objectDetailKey[currKey as keyof ExportModel] as keyof ExportModel}${index + 1}`]: value,
              [`**明細-仕切単価-${index + 1}`]: changeValueBySuryo(
                item.esd_suryo ?? 0,
                Math.floor(((item.esd_standard_price ?? 0) * (item.esd_net_rate ?? 0)) / (item.esd_suryo ?? 0) / 100)
              ),
            };
          }, {});

          return { ...newRow };
        }
      });

    const subtotalRow = (meisaiList: FilEstimaDetail[], hinType: number) => {
      const totalStandardPrice = meisaiList.reduce((prev: number, curr: FilEstimaDetail) => {
        if (!curr.esd_suryo) {
          return prev;
        } else {
          return prev + (curr.esd_standard_price ?? 0);
        }
      }, 0);
      const totalNetPrice = meisaiList.reduce((prev: number, curr: FilEstimaDetail) => {
        if (!curr.esd_suryo) {
          return prev;
        } else {
          return prev + (curr.esd_net_price ?? 0);
        }
      }, 0);
      const totalMaintenancePrice = meisaiList.reduce((prev: number, curr: FilEstimaDetail) => {
        if (!curr.esd_suryo) {
          return prev;
        } else {
          return prev + (curr.esd_maintenance_price ?? 0);
        }
      }, 0);
      return {
        esd_hin_nm: SUB_TOTAL,
        esd_hin_kb: hinType,
        esd_net_price: totalNetPrice,
        esd_standard_price: totalStandardPrice,
        esd_maintenance_price: totalMaintenancePrice,
        esd_hin_cd: 0,
      };
    };

    const yearFeeRow = (subTotalList: FilEstimaDetail[]) => {
      const yearFeeList: FilEstimaDetail[] = [];
      const totalStandardPrice = subTotalList.reduce((prev: number, curr: FilEstimaDetail) => {
        return prev + (curr.esd_standard_price ?? 0);
      }, 0);
      // const totalNetPrice = subTotalList.reduce((prev: number, curr: FilEstimaDetail) => {
      //   return prev + (curr.esd_net_price ?? 0);
      // }, 0);

      let title = '';
      let comment = '';
      if (mitsumoriInput.esp_contract_period === 1) {
        // 単年契約
        title = TOTAL;
        comment = `${(totalStandardPrice / mitsumoriInput.esp_contract_period).toLocaleString()}/年`;
      } else {
        // 複数年契約
        title = '＜ ' + mitsumoriInput.esp_contract_period + '　年　間　利　用　料　＞';
        comment = `${(totalStandardPrice / mitsumoriInput.esp_contract_period).toLocaleString()} × ${
          mitsumoriInput.esp_contract_period
        }`;

        if (mitsumoriInput.mcp_usage_fee_discount_rate !== 100) {
          comment =
            comment +
            '  ' +
            `※${mitsumoriInput.esp_contract_period}年一括契約による、${
              100 - mitsumoriInput.mcp_usage_fee_discount_rate
            }%割引適用済`;
        }
      }

      yearFeeList.push({
        esd_hin_nm: title,
        esd_hin_kb: 0,
        esd_hin_cd: 0,
      });

      yearFeeList.push({
        esd_hin_nm: comment,
        esd_hin_kb: 0,
        esd_hin_cd: 0,
      });

      return [...yearFeeList];
    };

    const subTotals: FilEstimaDetail[] = [];

    const addRows = (meisaiList: FilEstimaDetail[], hinType: number) => {
      if (hinType === MitsumoriType.DISCOUNT) {
        return [...meisaiList, { ...emptyRow }];
      } else {
        let title = '';
        switch (hinType) {
          case MitsumoriType.SOFTWARE:
            title = SOFTWARE;
            break;
          case MitsumoriType.TEMPLATE:
            title = TEMPLATE;
            break;
          case MitsumoriType.LICENSE:
            title = LICENSE;
            break;
          case MitsumoriType.USEWARE:
            title = USEWARE;
            break;
          case MitsumoriType.KDSS:
            title = KDSS;
            break;
          default:
        }
        // タイトルだけの場合は明細がないので、出力しない
        if (meisaiList.some((item) => item.esd_hin_nm !== title)) {
          const subTotal: FilEstimaDetail = subtotalRow(meisaiList, hinType);

          // 利用料製品の場合に合計を出す
          switch (hinType) {
            case MitsumoriType.SOFTWARE:
            case MitsumoriType.TEMPLATE:
            case MitsumoriType.LICENSE:
              subTotals.push(subTotal);
              break;
          }

          return [...meisaiList, { ...subTotal }, { ...emptyRow }];
        }
      }
      return [];
    };

    let allRowMeisai: FilEstimaDetail[] = [];

    const arrayField = [
      'softwareTableRows',
      'templateTableRows',
      'licenseTableRows',
      'usewareTableRows',
      'kdssTableRows',
      'discountTableRows',
    ];

    [
      MitsumoriType.SOFTWARE,
      MitsumoriType.TEMPLATE,
      MitsumoriType.LICENSE,
      MitsumoriType.USEWARE,
      MitsumoriType.KDSS,
      MitsumoriType.DISCOUNT,
    ].map((item, index) => {
      const meisaiRow = addRows(meisaiTableRows[arrayField[index] as keyof MeisaiTableRowProps], item);
      allRowMeisai = [...allRowMeisai, ...meisaiRow];
      if (
        item === MitsumoriType.LICENSE &&
        mitsumoriInput.mpr_discount_type === DiscountType.USING_FEE &&
        mitsumoriInput.esp_new_or_add_kb === MitsumoriKubun.REGISTER
      ) {
        // 利用料合計を出す(ライセンスの後、かつ利用料製品、かつ新規の場合)
        allRowMeisai = [...allRowMeisai, ...yearFeeRow(subTotals), { ...emptyRow }];
      }
      return allRowMeisai;
    });
    const newDetail = [...formatDetailData([...allRowMeisai])].reduce((prev, curr) => ({ ...prev, ...curr }), {});
    const newMeisaiTable = { ...newPoint, ...newDetail };

    return [newMeisaiTable, allRowMeisai.length];
  };

  const handleSelectGuideOffice = (office: MstOffice) => {
    setMitsumoriInput({
      ...mitsumoriInput,
      esp_office_cd: parseInt(office.mo_office_cd),
      mo_office_nm: office.mo_office_nm,
    });
  };

  const handleSelectGuideUser = (data: DataUser) => {
    setMitsumoriInput({
      ...mitsumoriInput,
      mu_user_name: data.mu_user_nm,
      mu_mail: data.mu_mail,
      esp_syain_cd: data.mu_user_id,
    });
  };

  const handleSelectPrimaryStore = (data: PrimaryStore) => {
    setMitsumoriInput({
      ...mitsumoriInput,
      esp_primary_nm: data.mps_primary_nm,
      mps_primary_nm: data.mps_primary_nm,
      esp_primary_cd: parseInt(data.mps_primary_cd),
      mss_secondary_nm: '',
      esp_secondary_nm: '',
      esp_secondary_cd: 0,
      esp_primary_rep: '',
      esp_secondary_rep: '',
    });
  };

  const handleSelectRemarks = (data: MstRemarks) => {
    const mitsumoriKeys = ['esp_sh_jyoken', 'esp_ex_date_st', 'esp_es_deli_date_st', 'esp_mainte_cont'];
    handleChangeMitsumoriInput({ [`${mitsumoriKeys[Number(mstRemarksKb) - 1]}`]: data.mrm_remarks });
    setError({ ...errors, [`${mitsumoriKeys[Number(mstRemarksKb) - 1]}`]: false });
  };

  // ガイドから選択された品名に紐づく品名情報をマージして呼び元に返す
  // 品名情報が存在しない場合は、空行になるようなオブジェクトを返す
  const mergeMstHinInfo = async (
    hinCode: string | number,
    primaryCode: number | undefined,
    secondaryCode: number | undefined,
    productCode: number | undefined,
    contractPeriod: number | undefined
  ) => {
    const mstHinInfo = await fetchMstHinInfo({
      variables: {
        input: {
          mhi_hin_cd: hinCode,
          mhi_product_cd: productCode,
          mhi_primary_cd: primaryCode,
          mhi_secondary_cd: secondaryCode,
          mhi_years: mitsumoriInput.mpr_discount_type === DiscountType.MAINTAIN_PRICE ? 0 : (contractPeriod ?? 0),
        },
      },
    });

    if (mstHinInfo.data.getMstHinInfo.length >= 1) {
      const hinInfo = mstHinInfo.data.getMstHinInfo[0];
      return {
        ...hinInfo,
        esd_maintenance_price: hinInfo.mhi_maintenance_price,
        esd_product_price: hinInfo.mhi_product_price,
        mhi_add_hinmei1: hinInfo.mhi_add_hinmei1,
        mhi_add_hinmei2: hinInfo.mhi_add_hinmei2,
        mhi_oso_cd: hinInfo.mhi_oso_cd,
        mhi_default_suryo: hinInfo.mhi_default_suryo,
      };
    }

    return { ...emptyHinInfo };
  };

  const createAsyncByTableRows = async (
    array: FilEstimaDetail[],
    row: string,
    primaryCode: number | undefined,
    secondaryCode: number | undefined
  ) => {
    const newArray = [...array];
    const firstItem = row !== 'discountTableRows' && row !== 'kdssTableRows' ? newArray.shift() : null;
    const arrayInput: MstHinInfo[] = [];
    newArray.map(async (item) => {
      const dataResponse: MstHinInfo = await mergeMstHinInfo(
        item.esd_hin_cd,
        primaryCode,
        secondaryCode,
        mitsumoriInput.esp_product_cd,
        mitsumoriInput.mpr_discount_type === DiscountType.MAINTAIN_PRICE ? 0 : mitsumoriInput.esp_contract_period
      );
      if (JSON.stringify(dataResponse) !== JSON.stringify(emptyHinInfo)) {
        arrayInput.push({ ...dataResponse, esd_hin_nm: item.esd_hin_nm });
      }
      if (firstItem) {
        setMeisaiTableRows((prev) => ({ ...prev, [row]: [firstItem && firstItem, ...arrayInput] }));
      } else {
        setMeisaiTableRows((prev) => ({ ...prev, [row]: [...arrayInput] }));
      }
    });
  };

  const handleCalcAllRowDetailMeisai = (primaryCode: number | undefined, secondaryCode: number | undefined) => {
    const fieldArr = [
      'discountTableRows',
      'licenseTableRows',
      'softwareTableRows',
      'templateTableRows',
      'usewareTableRows',
    ];
    fieldArr.forEach((key: string) => handleCalcDetailByMstHinInfo(key, primaryCode, secondaryCode));
  };

  const handleCalcDetailByMstHinInfo = (
    row: string,
    primaryCode: number | undefined,
    secondaryCode: number | undefined
  ) => {
    createAsyncByTableRows(meisaiTableRows[row as keyof MeisaiTableRowProps], row, primaryCode, secondaryCode);
  };

  const handleSelectSecondaryStore = (data: SecondaryStore) => {
    handleCalcAllRowDetailMeisai(mitsumoriInput.esp_primary_cd, parseInt(data.mss_secondary_cd));
    setError({ ...errors, esp_secondary_cd: false });
    fetchMstNetRate({
      variables: {
        input: {
          mnr_product_cd: mitsumoriInput.esp_product_cd,
          mnr_primary_cd: mitsumoriInput.esp_primary_cd,
          mnr_secondary_cd: data.mss_secondary_cd,
        },
      },
    }).then((res) => {
      const { getMstNetRate: netRateData } = res.data;

      setMitsumoriInput((prev) => ({
        ...prev,
        mss_secondary_nm: data.mss_secondary_nm,
        esp_secondary_nm: data.mss_secondary_nm,
        esp_secondary_cd: parseInt(data.mss_secondary_cd),
        mnr_software_net_rate: netRateData[0]?.mnr_software_net_rate,
        mnr_template_net_rate: netRateData[0]?.mnr_template_net_rate,
        mnr_license_net_rate: netRateData[0]?.mnr_license_net_rate,
        mnr_useware_net_rate: netRateData[0]?.mnr_useware_net_rate,
        mnr_maintenance_net_rate: netRateData[0]?.mnr_maintenance_net_rate,
        esp_template_net_rate: netRateData[0]?.mnr_template_net_rate,
        esp_useware_net_rate: netRateData[0]?.mnr_useware_net_rate,
        esp_software_net_rate: netRateData[0]?.mnr_software_net_rate,
        esp_license_net_rate: netRateData[0]?.mnr_license_net_rate,
        esp_maintenance_net_rate: netRateData[0]?.mnr_maintenance_net_rate,
      }));
    });
  };

  const handleSelectProductDivision = (data: MprProduct) => {
    handleChangeMitsumoriInput({
      mpr_product_nm: data.mpr_product_nm,
      mpr_discount_type: data.mpr_discount_type,
      mpr_maintenance_limit: data.mpr_maintenance_limit,
      esp_product_cd: data.mpr_product_cd,
    });
    fetchMstNetRate({
      variables: {
        input: {
          mnr_product_cd: data.mpr_product_cd,
          mnr_primary_cd: mitsumoriInput.esp_primary_cd,
          mnr_secondary_cd: mitsumoriInput.esp_secondary_cd,
        },
      },
    }).then((res) => {
      const { getMstNetRate: netRateData } = res.data;
      setMitsumoriInput((prev) => ({
        ...prev,
        mnr_software_net_rate: netRateData[0]?.mnr_software_net_rate,
        mnr_template_net_rate: netRateData[0]?.mnr_template_net_rate,
        mnr_license_net_rate: netRateData[0]?.mnr_license_net_rate,
        mnr_useware_net_rate: netRateData[0]?.mnr_useware_net_rate,
        mnr_maintenance_net_rate: netRateData[0]?.mnr_maintenance_net_rate,
        esp_template_net_rate: netRateData[0]?.mnr_template_net_rate,
        esp_useware_net_rate: netRateData[0]?.mnr_useware_net_rate,
        esp_software_net_rate: netRateData[0]?.mnr_software_net_rate,
        esp_license_net_rate: netRateData[0]?.mnr_license_net_rate,
        esp_maintenance_net_rate: netRateData[0]?.mnr_maintenance_net_rate,
      }));
    });
    setError({ ...errors, esp_product_cd: false });
  };

  const handleSelectContractPeriod = (data: ContractPeriod) => {
    handleChangeMitsumoriInput({
      esp_contract_period: data.mcp_years,
      mcp_usage_fee_discount_rate: data.mcp_usage_fee_discount_rate,
      mcp_maintenance_discount_rate: data.mcp_maintenance_discount_rate,
    });
  };

  const handleClickAdd = (type: MitsumoriType) => {
    setHinmeiKbSelected(type);
    // 値引き行以外はガイドを開く
    if (type !== MitsumoriType.DISCOUNT) {
      setIsShowModaGuideHinmei(true);
    } else handleAddFreeInputRow('');
  };

  const handleDataFilEstimaDetail = (data: FilEstimaDetail[]) => {
    const getArrData = (type?: number) => {
      let newArr = data.filter((item: FilEstimaDetail) => item.esd_hin_kb === type) || [];
      if (!type) {
        newArr =
          data.filter((item: FilEstimaDetail) => {
            return [
              MitsumoriType.SOFTWARE,
              MitsumoriType.LICENSE,
              MitsumoriType.USEWARE,
              MitsumoriType.TEMPLATE,
              MitsumoriType.KDSS,
            ].every((i) => i !== item.esd_hin_kb);
          }) || [];
      }

      return newArr.map((item: FilEstimaDetail) => ({
        ...item,
        isFreeInput: item.esd_hin_cd >= hinmeiCdInputFree,
        rowInputSettings: {
          esd_hin_nm: {
            type: 'text',
            disabled: item.isFreeName === 0 ? true : false,
          },
          esd_suryo: {
            type: 'number',
            autoPreFix: false,
          },
          esd_standard_price: {
            type: 'text',
            formatCurrency: true,
            maxLength: 11,
          },
          esd_net_rate: {
            autoPreFix: false,
            type: 'number',
          },
          esd_net_price: {
            type: 'text',
            formatCurrency: true,
            maxLength: 11,
          },
          esd_maintenance_price: {
            type: 'text',
            formatCurrency: true,
            maxLength: 11,
          },
          esd_net_reason: {
            type: 'text',
          },
        },
      }));
    };

    const filEstimaDetailData = {
      softwareTableRows: [
        { esd_hin_nm: '＜ソフトウェア＞', esd_hin_cd: 0, isFreeInput: false },
        ...getArrData(MitsumoriType.SOFTWARE),
      ],
      licenseTableRows: [
        { esd_hin_nm: '＜ライセンス＞', esd_hin_cd: 0, isFreeInput: false },
        ...getArrData(MitsumoriType.LICENSE),
      ],
      usewareTableRows: [
        { esd_hin_nm: '＜ユースウェア＞', esd_hin_cd: 0, isFreeInput: false },
        ...getArrData(MitsumoriType.USEWARE),
      ],
      templateTableRows: [
        { esd_hin_nm: '＜テンプレート＞', esd_hin_cd: 0, isFreeInput: false },
        ...getArrData(MitsumoriType.TEMPLATE),
      ],
      kdssTableRows: [
        { esd_hin_nm: '＜KDSS保守＞', esd_hin_cd: 0, isFreeInput: false },
        ...getArrData(MitsumoriType.KDSS),
      ],
      discountTableRows: getArrData(),
    };
    setMeisaiTableRows({ ...meisaiTableRows, ...filEstimaDetailData });
  };

  const handleAddFreeInputRow = async (data: Array<MstHinmei> | MstHinmeiFree | string) => {
    const targetRow = selectedRow[hinmeiKbSelected];
    let keyOfTypeProduct = '';
    let dataNetRate = 0;
    switch (hinmeiKbSelected) {
      case MitsumoriType.TEMPLATE:
        keyOfTypeProduct = 'templateTableRows';
        dataNetRate = mitsumoriInput.esp_template_net_rate;
        break;
      case MitsumoriType.LICENSE:
        keyOfTypeProduct = 'licenseTableRows';
        dataNetRate = mitsumoriInput.esp_license_net_rate;
        break;
      case MitsumoriType.SOFTWARE:
        keyOfTypeProduct = 'softwareTableRows';
        dataNetRate = mitsumoriInput.esp_software_net_rate;
        break;
      case MitsumoriType.USEWARE:
        keyOfTypeProduct = 'usewareTableRows';
        dataNetRate = mitsumoriInput.esp_useware_net_rate;
        break;
      case MitsumoriType.DISCOUNT:
        keyOfTypeProduct = 'discountTableRows';
        break;
      default:
    }
    if ((data as MstHinmeiFree).mh_hin_cd === '-1' || hinmeiKbSelected === MitsumoriType.DISCOUNT) {
      const freeItemList = meisaiTableRows[keyOfTypeProduct as keyof MeisaiTableRowProps].filter(
        (item) => item.esd_hin_cd >= emptyRow.esd_hin_cd
      );

      let freeRow: MeisaiRowProps = {
        ...emptyRow,
        esd_hin_kb: hinmeiKbSelected,
        esd_net_rate: dataNetRate,
      };

      if (hinmeiKbSelected === MitsumoriType.DISCOUNT) {
        freeRow = { ...freeRow, esd_net_rate: 100, esd_hin_kb: 9 };
      }

      if (freeItemList.length > 0) {
        const maxOfFreeItemList = Math.max(...freeItemList.map((row) => row.esd_hin_cd));
        freeRow = { ...freeRow, esd_hin_cd: maxOfFreeItemList + 1 };
      }

      // 行追加処理を行う時点で、その対象テーブルの行が選択されていれば、指定行に追加処理を行う
      // 上記以外の場合は、従来通り、最下行に追加が行われる
      if (targetRow) {
        const target = [...meisaiTableRows[keyOfTypeProduct as keyof MeisaiTableRowProps]];
        target.splice(targetRow + 1, 0, freeRow);
        setMeisaiTableRows({
          ...meisaiTableRows,
          [keyOfTypeProduct]: target,
        });
      } else {
        setMeisaiTableRows({
          ...meisaiTableRows,
          [keyOfTypeProduct]: [...meisaiTableRows[keyOfTypeProduct as keyof MeisaiTableRowProps], freeRow],
        });
      }
    }
    const arrayInput: MeisaiRowProps[] = [];
    if ((data as Array<MstHinmei>).length > 0) {
      for (const item of data as Array<MstHinmei>) {
        const newHinmeiRow: MstHinInfo = await mergeMstHinInfo(
          item.mh_hin_cd,
          mitsumoriInput.esp_primary_cd,
          mitsumoriInput.esp_secondary_cd,
          mitsumoriInput.esp_product_cd,
          mitsumoriInput.mpr_discount_type === DiscountType.MAINTAIN_PRICE ? 0 : mitsumoriInput.esp_contract_period
        );

        // 品名情報が空の場合は、追加を行わない
        if (JSON.stringify(newHinmeiRow) !== JSON.stringify(emptyHinInfo)) {
          if (
            !meisaiTableRows[keyOfTypeProduct as keyof MeisaiTableRowProps].some(
              (row: FilEstimaDetail) => row.esd_hin_cd === newHinmeiRow?.mhi_hin_cd
            )
          ) {
            const calcRemainingMonthPrice = (price: number, discount: number) => {
              if (
                mitsumoriInput.esp_new_or_add_kb === MitsumoriKubun.REGISTER ||
                mitsumoriInput.mpr_discount_type === DiscountType.MAINTAIN_PRICE ||
                hinmeiKbSelected === MitsumoriType.USEWARE
              ) {
                return price;
              } else {
                console.log('price', price);
                console.log('discount', discount);
                console.log('mitsumoriInput.esp_remaining_month', mitsumoriInput.esp_remaining_month);
                console.log('((price / 12))', price / 12);
                console.log(
                  'Math.round((price / 12) * mitsumoriInput.esp_remaining_month * (discount / 100))',
                  Math.round((price / 12) * mitsumoriInput.esp_remaining_month * (discount / 100))
                );
                return Math.round((price / 12) * mitsumoriInput.esp_remaining_month * (discount / 100));
              }
            };
            const qty = newHinmeiRow.mhi_default_suryo ?? 1;
            arrayInput.push({
              ...newHinmeiRow,
              isFreeName: item.mh_freename_kb,
              isFreeInput: false,
              esd_net_rate: dataNetRate,
              esd_hin_nm: item.mh_hin_nm,
              esd_hin_cd: newHinmeiRow?.mhi_hin_cd,
              esd_product_price: calcRemainingMonthPrice(newHinmeiRow?.mhi_product_price ?? 0, 100),
              esd_net_reason: '',
              esd_hin_kb: hinmeiKbSelected,
              esd_maintenance_price: calcRemainingMonthPrice((newHinmeiRow?.mhi_maintenance_price ?? 0) * qty, 100),
              esd_net_price: calcRemainingMonthPrice(
                Math.floor(((newHinmeiRow?.mhi_product_price ?? 0) * qty * dataNetRate) / 100),
                100
              ),
              esd_standard_price: calcRemainingMonthPrice((newHinmeiRow?.mhi_product_price ?? 0) * qty, 100),
              esd_suryo: qty,
              rowInputSettings: baseRowInputSettings,
            });
          } else {
            await utility.showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
          }
        } else {
          await utility.showAlertMessage('', InfoMessage.NOT_EXIST_HIN_INFO, InfoWord.ERROR, InfoWord.OK);
        }
      }
      if (arrayInput.length >= 1) {
        // 行追加処理を行う時点で、その対象テーブルの行が選択されていれば、指定行に追加処理を行う
        // 上記以外の場合は、従来通り、最下行に追加が行われる
        if (targetRow) {
          const target = [...meisaiTableRows[keyOfTypeProduct as keyof MeisaiTableRowProps]];
          target.splice(targetRow + 1, 0, ...arrayInput);
          setMeisaiTableRows({
            ...meisaiTableRows,
            [keyOfTypeProduct]: target,
          });
        } else {
          setMeisaiTableRows({
            ...meisaiTableRows,
            [keyOfTypeProduct]: [...meisaiTableRows[keyOfTypeProduct as keyof MeisaiTableRowProps], ...arrayInput],
          });
        }
      }
    }

    setMitsumoriInput({
      ...mitsumoriInput,
      status: mitsumoriInput.status === RecordState.NONE ? RecordState.NONE : mitsumoriInput.status,
    });
  };

  const handleCalcKdssDetail = async () => {
    const mstHinmeiDataNew = { ...mstHinmeiData };
    const totalPriceTableDataNew = [...totalPriceTableData];
    const mitsumoriInputNew = { ...mitsumoriInput };
    const hinmeiDataFromDB = mstHinmeiDataNew.getMstHinmei || [];
    let kdssRowData: FilEstimaDetail[] = [];
    let hinName = '';

    if ((totalPriceTableData[4].basePrice ?? 0) === 0) {
      // 保守金額が0円なら計算対象外
      const kdssTableRows: FilEstimaDetail[] = [];
      setMeisaiTableRows((prev) => ({ ...prev, kdssTableRows: kdssTableRows })); // TODO:クリアできない
      return;
    }

    // コメント行の生成 TODO:コメントいるか再検討中
    const maitainTotalPrice =
      ((totalPriceTableDataNew[0]?.maintainPrice ?? 0) * mitsumoriInput.mcp_maintenance_discount_rate) / 100 +
      ((totalPriceTableDataNew[1]?.maintainPrice ?? 0) * mitsumoriInput.mcp_maintenance_discount_rate) / 100 +
      ((totalPriceTableDataNew[2]?.maintainPrice ?? 0) * mitsumoriInput.mcp_maintenance_discount_rate) / 100;

    if (mitsumoriInput.esp_contract_period === 1) {
      // 単年契約
      hinName = `${maitainTotalPrice.toLocaleString()} /年`;
    } else {
      // 複数年契約
      // hinName = `${maitainTotalPrice.toLocaleString()} × ${mitsumoriInputNew.esp_contract_period}`;
      hinName = `${(
        (totalPriceTableData[4].basePrice ?? 0) / mitsumoriInputNew.esp_contract_period
      ).toLocaleString()} × ${mitsumoriInputNew.esp_contract_period}`;

      if (mitsumoriInput.mcp_maintenance_discount_rate !== 100) {
        hinName =
          hinName +
          '  ' +
          `※${mitsumoriInput.esp_contract_period}年一括契約による、${
            100 - mitsumoriInput.mcp_maintenance_discount_rate
          }%割引適用済`;
      }
    }

    const commentRow: FilEstimaDetail = {
      esd_net_reason: '',
      esd_hin_cd: hinmeiCdInputFree,
      esd_hin_nm: hinName,
      esd_hin_kb: MitsumoriType.KDSS,
    };

    // KDSS保守行の生成
    const promiseArr: FilEstimaDetail[] = [];

    for (const rowInfo of totalPriceTableData as Array<TotalPriceTableProps>) {
      if (Number(rowInfo.type) >= 4) break; // 対象はソフトウェア、テンプレート、ライセンスのみ
      if ((rowInfo.kdssBasePrice ?? 0) === 0) continue;

      if (!mitsumoriInputNew.esp_divide_maintenance) {
        // 保守単位で明細を分けない
        const hinmeiHasDivisibleToKdssPrice = hinmeiDataFromDB.filter(
          (item: MstHinmei) =>
            item.mh_maintenance_tani &&
            item.mh_maintenance_kb === rowInfo.type &&
            (rowInfo.kdssBasePrice ?? 0) % Number(item.mh_maintenance_tani) === 0
        );

        const listMaintenanceTani = hinmeiHasDivisibleToKdssPrice.map((item: MstHinmei) => item.mh_maintenance_tani);
        const hinmeiWithMaxValue = hinmeiHasDivisibleToKdssPrice.find(
          (hinmei: MstHinmei) => hinmei.mh_maintenance_tani === Math.max(...listMaintenanceTani)
        );

        await fetchMstHinInfo({
          variables: {
            input: {
              mhi_hin_cd: hinmeiWithMaxValue.mh_hin_cd,
              mhi_product_cd: mitsumoriInputNew.esp_product_cd,
              mhi_primary_cd: mitsumoriInputNew.esp_primary_cd,
              mhi_secondary_cd: mitsumoriInputNew.esp_secondary_cd,
              mhi_years:
                mitsumoriInputNew.mpr_discount_type === DiscountType.MAINTAIN_PRICE
                  ? 0
                  : (mitsumoriInputNew.esp_contract_period ?? 0),
            },
          },
        }).then((res) => {
          if (res.data.getMstHinInfo.length <= 0) return;

          const hinInfo: MstHinInfo = res.data.getMstHinInfo[0];
          const qty = (rowInfo.kdssBasePrice ?? 0) / Number(hinmeiWithMaxValue?.mh_maintenance_tani);
          promiseArr.push({
            esd_hin_kb: MitsumoriType.KDSS,
            esd_hin_cd: hinInfo.mhi_hin_cd,
            esd_hin_nm: hinmeiWithMaxValue.mh_hin_nm,
            esd_net_price: rowInfo.kdssDetailPrice,
            esd_net_rate: mitsumoriInputNew.esp_maintenance_net_rate,
            esd_net_reason: '',
            esd_maintenance_price: hinInfo.mhi_maintenance_price,
            esd_product_price: hinInfo.mhi_product_price,
            esd_standard_price: rowInfo.kdssBasePrice,
            mhi_add_hinmei1: hinInfo.mhi_add_hinmei1,
            mhi_add_hinmei2: hinInfo.mhi_add_hinmei2,
            mhi_oso_cd: hinInfo.mhi_oso_cd,
            mhi_product_price: hinInfo.mhi_product_price,
            esd_suryo: qty,
          });
        });
      } else {
        // 保守単位で明細を分ける
        const hinmeiHasDivisibleToKdssPrice = hinmeiDataFromDB.filter(
          (item: MstHinmei) => item.mh_maintenance_tani && item.mh_maintenance_kb === rowInfo.type
        );

        let priceNum = Number(rowInfo.kdssBasePrice);
        let arrSorted =
          hinmeiHasDivisibleToKdssPrice.sort(
            (a: MstHinmei, b: MstHinmei) => a.mh_maintenance_tani - b.mh_maintenance_tani
          ) || [];
        let mhTani = 0;
        do {
          const mod = priceNum;
          const listHasHinmeiLessThanKdssPrice = arrSorted.filter(
            (item: MstHinmei) => mod > Number(item.mh_maintenance_tani)
          );
          const hinmeiWithMaxValue = listHasHinmeiLessThanKdssPrice.pop();

          arrSorted = [...listHasHinmeiLessThanKdssPrice];
          mhTani = hinmeiWithMaxValue?.mh_maintenance_tani;
          if (!mhTani) return;
          const priceNumConst = priceNum;

          await fetchMstHinInfo({
            variables: {
              input: {
                mhi_hin_cd: hinmeiWithMaxValue?.mh_hin_cd,
                mhi_product_cd: mitsumoriInputNew.esp_product_cd,
                mhi_primary_cd: mitsumoriInputNew.esp_primary_cd,
                mhi_secondary_cd: mitsumoriInputNew.esp_secondary_cd,
                mhi_years:
                  mitsumoriInputNew.mpr_discount_type === DiscountType.MAINTAIN_PRICE
                    ? 0
                    : (mitsumoriInputNew.esp_contract_period ?? 0),
              },
            },
          }).then((res) => {
            if (res.data.getMstHinInfo.length <= 0) return;

            const hinInfo: MstHinInfo = res.data.getMstHinInfo[0];
            const quantity = Math.floor(priceNumConst / Number(hinmeiWithMaxValue?.mh_maintenance_tani));
            promiseArr.push({
              esd_hin_cd: hinInfo.mhi_hin_cd,
              esd_hin_nm: hinmeiWithMaxValue?.mh_hin_nm,
              esd_net_price: hinInfo.mhi_product_price * quantity * (mitsumoriInput.esp_maintenance_net_rate / 100),
              esd_net_rate: mitsumoriInputNew.esp_maintenance_net_rate,
              esd_net_reason: '',
              esd_maintenance_price: hinInfo.mhi_maintenance_price,
              esd_product_price: hinInfo.mhi_product_price,
              esd_standard_price: hinInfo.mhi_product_price * quantity,
              mhi_add_hinmei1: hinInfo.mhi_add_hinmei1,
              mhi_add_hinmei2: hinInfo.mhi_add_hinmei2,
              mhi_oso_cd: hinInfo.mhi_oso_cd,
              mhi_product_price: hinInfo.mhi_product_price,
              esd_suryo: quantity,
              esd_hin_kb: MitsumoriType.KDSS,
            });
          });
          priceNum = priceNum - Math.floor(priceNum / mhTani) * mhTani;
        } while (priceNum && priceNum !== 0 && mhTani);
      }
    }

    const newDataKdssRow = [...promiseArr].filter((item) => item);
    kdssRowData = [...newDataKdssRow];
    setMeisaiTableRows((prev) => ({
      ...prev,
      kdssTableRows: [meisaiTableRows.kdssTableRows[0], ...kdssRowData, commentRow],
    }));
    meisaiTableRows.kdssTableRows = [meisaiTableRows.kdssTableRows[0], ...kdssRowData, commentRow];
  };

  const prepareDataApplyApi = () => {
    const {
      discountTableRows,
      kdssTableRows,
      licenseTableRows,
      softwareTableRows,
      templateTableRows,
      usewareTableRows,
    } = meisaiTableRows;
    const detailInfo = [
      ...softwareTableRows,
      ...templateTableRows,
      ...licenseTableRows,
      ...usewareTableRows,
      ...kdssTableRows,
      ...discountTableRows,
    ].filter((item: MeisaiRowProps) => item.esd_hin_cd !== 0);

    const omitArr = [
      'status',
      'mcp_maintenance_discount_rate',
      'mcp_usage_fee_discount_rate',
      'mnr_license_net_rate',
      'mnr_maintenance_net_rate',
      '__typename',
      'mnr_software_net_rate',
      'mnr_template_net_rate',
      'mnr_useware_net_rate',
      'mo_office_nm',
      'mpr_discount_type',
      'mpr_maintenance_limit',
      'mpr_product_nm',
      'mps_primary_nm',
      'mss_secondary_nm',
      'mu_mail',
      'mu_user_name',
      'mpr_available_kb',
      'mpr_product_cd',
      'mo_post',
      'mo_address',
      'mo_fax',
      'mo_office_cd',
      'mo_post',
      'mo_tel',
    ];

    const newMitsumoriInput: MitsumoriInput = { ...mitsumoriInput };

    // 値引関連の率を反映
    newMitsumoriInput.esp_software_discount_rate = discountInput.software;
    newMitsumoriInput.esp_template_discount_rate = discountInput.template;
    newMitsumoriInput.esp_license_discount_rate = discountInput.license;
    newMitsumoriInput.esp_useware_discount_rate = discountInput.useware;
    newMitsumoriInput.esp_maintenance_discount_rate = discountInput.kdssMaintenance;

    const point = Object.keys(newMitsumoriInput).reduce((prev: Partial<MitsumoriInput>, curr: string) => {
      const isFieldOmit = omitArr.every((field) => curr !== field);

      if (isFieldOmit) return { ...prev, [curr]: newMitsumoriInput[curr as keyof MitsumoriInput] };
      return { ...prev };
    }, {});

    return {
      point,
      detail: detailInfo.map((item: FilEstimaDetail) => {
        const newItem = {
          esd_est_kb_no: point.esp_est_kb_no,
          esd_estima_no: point.esp_estima_no,
          esd_hin_cd: item.esd_hin_cd,
          esd_hin_kb: item.esd_hin_kb,
          esd_hin_nm: item.esd_hin_nm,
          esd_maintenance_price: item.esd_maintenance_price,
          esd_net_price: item.esd_net_price,
          esd_net_rate: item.esd_net_rate,
          esd_net_reason: item.esd_net_reason,
          esd_product_price: item.esd_product_price,
          esd_standard_price: item.esd_standard_price,
          esd_suryo: item.esd_suryo,
        };

        return newItem;
      }),
    };
  };

  const handleRegister = async () => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        utility.confirmAlert(
          InfoMessage.CONFIRM_REGISTR,
          '',
          InfoWord.WARNING,
          InfoWord.YES,
          InfoWord.NO,
          sendRegisterRequest
        );
      })
      .catch((err) => {
        signOut();
        navigate('/login', { replace: true, state: { from: location } });
        console.log(err);
        return;
      });
  };

  const sendRegisterRequest = async () => {
    setIsProcessing(true);
    if (!validator()) {
      setIsProcessing(false);
      return;
    }

    await handleCalcKdssDetail();
    registerEstima({ variables: { input: prepareDataApplyApi() } })
      .then((res) => {
        if (res.data) {
          initEditData(mitsumoriInput.esp_estima_no, mitsumoriInput.esp_est_kb_no);
          utility.showAlertMessage('', InfoMessage.REGISTR_SUCCESS, InfoWord.SUCCESS, InfoWord.OK);
        } else {
          utility.showAlertMessage('', ErrorMessage.REGISTR_FAILED, InfoWord.ERROR, InfoWord.OK);
        }
      })
      .catch((err) => {
        utility.showAlertMessage('', ErrorMessage.REGISTR_FAILED, InfoWord.ERROR, InfoWord.OK);
        console.log(err);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  const getReportFiles = async () => {
    try {
      const currSession = await Auth.currentSession();
      const jwtToken = currSession.getAccessToken().getJwtToken();
      const url = `${process.env.REACT_APP_API_GW_END_POINT}/${process.env.NODE_ENV}/get-report-template-files`;

      const res = await fetch(url, {
        headers: { Authorization: jwtToken as string },
      });
      const data = await res.json();
      const options = data.map((item: string) => ({ label: item, value: item }));
      setPrintOptions(options);
    } catch (err) {
      signOut();
      navigate('/login', { replace: true, state: { from: location } });
      console.log(err);
    }
  };

  const initNewData = async () => {
    // user情報を取得する
    const resUser = await searchMstUser({ variables: { input: { mu_mail: user.username } } });
    const mstUser: MstUser = resUser.data.getMstUser[0];

    // 有効期限を取得する
    const resRemarks = await searchMstRemarks({ variables: { input: { mrm_remarks_kb: 2, mrm_remarks_cd: 21 } } }); // remarks_cd:21をデフォルトとする
    const mstRemarks: MstRemarks = resRemarks.data.getMstRemarks[0];

    if (!mstUser) {
      // ユーザー情報が取得できない場合は、採番処理などに影響があるため、見積処理自体を行えなくする。
      utility.showAlertMessage('', InfoMessage.NOT_EXIST_USER, InfoWord.ERROR, InfoWord.YES);
      navigate(pathURL.QUOTATION_LIST.pathName, { replace: true, state: { from: location } });
      return;
    }

    // useridを利用して採番を行うので取得できない場合は何もしない
    if (!user.username) return;
    fetchEspRegisterNo({ variables: { input: { mu_user_id: Number(mstUser.mu_user_id) } } }).then((res) => {
      // XXXX0001番の採番に関しては、直接行う
      let estimaNo = Number(`${mstUser.mu_user_id}0001`);
      if (res.data.getEspRegisterNo[0]?.esp_estima_no) estimaNo = res.data.getEspRegisterNo[0].esp_estima_no;

      setMitsumoriInput((prev) => ({
        ...prev,
        esp_estima_no: estimaNo,
        esp_est_kb_no: 0,
        esp_est_date: moment(new Date()).format('YYYY-MM-DD'),
        status: RecordState.INSERT,
        esp_syain_cd: Number(mstUser.mu_user_id),
        mu_user_name: mstUser.mu_user_nm,
        esp_office_cd: Number(mstUser.mu_office_cd),
        mu_mail: mstUser.mu_mail,
        esp_ex_date_st: mstRemarks.mrm_remarks,
      }));
      handleDataFilEstimaDetail([]);
    });
  };

  const initEditData = async (kojiNo: number, kbNo: number) => {
    // ヘッダーデータを格納
    const resPoint = await fetchFilEstimaPoint({
      variables: { input: { esp_estima_no: kojiNo, esp_est_kb_no: kbNo } },
    });
    const filPoint = resPoint.data.getFilEstimaPoint[0];
    // 見積に登録されている社員IDからメールアドレスとユーザー名を取得する
    // 社員IDをパラメーターに入れた場合、クエリ自体はLIKEで取得されるが、社員IDが被るケースは存在せず、また複数取得できた場合も
    // 昇順にして最初のデータを参照するため、問題は出ないはず。
    const resUser = await searchMstUser({ variables: { input: { mu_user_id: filPoint.esp_syain_cd } } });
    const userList = resUser.data.getMstUser;
    if (resUser.data.getMstUser.length >= 2)
      userList.sort((a: MstUser, b: MstUser) => Number(a.mu_user_id) - Number(b.mu_user_id));

    const mstUser = userList[0];

    const mitsumoriData = {
      ...filPoint,
      status: RecordState.NONE,
    };

    const resProduct = await searchMstProduct({
      variables: {
        input: {
          mpr_product_cd: mitsumoriData.esp_product_cd,
        },
      },
    });
    const product = resProduct.data.getMstProduct[0];

    setMitsumoriInput((prev) => ({
      ...prev,
      ...mitsumoriData,
      ...product,
      mu_user_name: mstUser.mu_user_nm,
      mu_mail: mstUser.mu_mail,
    }));

    setDiscountInput((prev) => ({
      ...prev,
      software: mitsumoriData.esp_software_discount_rate,
      template: mitsumoriData.esp_template_discount_rate,
      license: mitsumoriData.esp_license_discount_rate,
      useware: mitsumoriData.esp_useware_discount_rate,
      kdssMaintenance: mitsumoriData.esp_maintenance_discount_rate,
    }));

    // 明細データ取得
    const resDetail = await fetchFilEstimaDetail({
      variables: { input: { esd_estima_no: kojiNo, esd_est_kb_no: kbNo } },
    });
    let filDetail = resDetail.data.getFilEstimaDetail;

    if (filDetail.length >= 1) {
      const resHinmei = await fetchMstHinmei();
      const mstHinmei = resHinmei.data.getMstHinmei;
      filDetail = filDetail.map((row: FilEstimaDetail) => {
        const foundHinmei = mstHinmei.find((hinmei: MstHinmei) => row.esd_hin_cd === Number(hinmei.mh_hin_cd));
        return { ...row, isFreeName: foundHinmei?.mh_freename_kb };
      });

      const resHinInfo = await fetchMstHinInfoFull({
        variables: {
          input: {
            mhi_primary_cd: mitsumoriData.esp_primary_cd,
            mhi_secondary_cd: mitsumoriData.esp_secondary_cd,
            mhi_product_cd: mitsumoriData.esp_product_cd,
            mhi_years:
              product.mpr_discount_type === DiscountType.MAINTAIN_PRICE ? 0 : (mitsumoriData.esp_contract_period ?? 0),
          },
        },
      });
      const mstHinInfo = resHinInfo.data.getMstHinInfo;
      filDetail = filDetail.map((row: FilEstimaDetail) => {
        const foundHinInfo = mstHinInfo.find((hininfo: MstHinInfo) => row.esd_hin_cd === Number(hininfo.mhi_hin_cd));
        return {
          ...row,
          mhi_product_price: foundHinInfo?.mhi_product_price,
          mhi_add_hinmei1: foundHinInfo?.mhi_add_hinmei1,
          mhi_add_hinmei2: foundHinInfo?.mhi_add_hinmei2,
          mhi_oso_cd: foundHinInfo?.mhi_oso_cd,
          mhi_maintenance_price: foundHinInfo?.mhi_maintenance_price,
        };
      });
    }

    handleDataFilEstimaDetail(filDetail);
  };

  const setColor = (rowType: number | undefined) => {
    if (rowType) {
      return rowTableColor[rowType];
    } else return '#FFF';
  };

  const renderInputArea = useCallback(
    () => (
      <div className=" mx-auto max-w-[84%] flex flex-wrap my-6">
        {inputList.map((input: any, index: number) => (
          <div key={index} className={`flex items-center w-full mt-6 ${input.className}`}>
            {input.type === 'button-group' &&
              input.buttonList.map((button: ButtonListType, index: number) => (
                <button key={index} onClick={button.onClick} className={button.className}>
                  {button.label}
                </button>
              ))}
            {input.type !== 'button-group' && (
              <>
                <label
                  className={
                    'mr-6 py-4 block text-center  w-[140px] bg-[#a8d08d] rounded-[3px] text-white' +
                    (input.error ? '!bg-[red]' : '')
                  }
                >
                  {input.label}
                </label>
                {(input.type === 'input' || input.type === 'number') && (
                  <EstimaInput
                    className={`border h-[56px] ${input.fieldlength}`}
                    type={input.type === 'input' ? '' : input.type}
                    value={input.value || input.checked || ''}
                    onChangeInput={input.onChange}
                    disabled={input.disabled}
                    autoPreFix={input.autoPreFix}
                    maxLength={input.maxLength}
                  />
                )}

                {input.type === 'checkbox' && (
                  <EstimaInput
                    className="border !h-[56px]"
                    type={input.type}
                    value={input.checked}
                    checked={input.checked}
                    onChangeInput={input.onChange}
                    disabled={input.disabled}
                    autoPreFix={input.autoPreFix}
                  />
                )}

                {input.type === 'date' && <DatetimePicker value={input.value} handleChange={input.onChange} />}

                {input.type === 'range-custom' && (
                  <RangeInput
                    from={input.from}
                    to={input.to}
                    inputClass={input.inputClass}
                    onChangeFrom={input.onChangeFrom}
                    disabledTo={input.disabledTo}
                    disabledFrom={input.disabledFrom}
                    autoPreFix={Boolean(input.autoPreFix)}
                    delimiter={input.delimiter ?? Delimiter.HYPHEN}
                  />
                )}

                {input.type === 'select' && (
                  <Selection
                    selected={input.value || { label: '', value: '' }}
                    options={input.options}
                    onChange={input.onChange}
                    className={input.fieldlength}
                  />
                )}

                {input.type === 'select-guide' && (
                  <p
                    title={input.value}
                    onClick={input.disabled ? undefined : input.onClick}
                    className={
                      (input.disabled ? 'bg-neutral-300' : 'bg-white') +
                      ` py-4 border min-h-[56px] rounded-[3px] px-2 ${input.fieldlength} truncate cursor-pointer`
                    }
                  >
                    {input.value}
                  </p>
                )}

                {input.type === 'custom-guide' && (
                  <>
                    <EstimaInput
                      className={`border h-[56px] ${input.fieldlength}`}
                      type={input.type === 'input' ? '' : input.type}
                      value={input.value || input.checked || ''}
                      onChangeInput={input.onChange}
                      disabled={input.disabled}
                      autoPreFix={input.autoPreFix}
                      maxLength={input.maxLength}
                    />

                    <img
                      src={images.icGuide}
                      alt="guide_icon"
                      className="w-[20px] cursor-pointer mx-3"
                      onClick={input.disabled ? undefined : input.onClick}
                    />
                  </>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    ),
    [mitsumoriInput, printOptions, errors]
  );

  const addNewRow = (rowType: MitsumoriType) => {
    return (
      <tr className={`border-x-[1px] border-[#ccc] border-b-[1px] ${rowTableColor[rowType]}`}>
        <td colSpan={8} className="text-center p-2">
          <img
            src={images.icPlus}
            alt=""
            className="w-[40px] mx-auto block cursor-pointer"
            onClick={() => handleClickAdd(rowType)}
          />
        </td>
      </tr>
    );
  };

  const renderHeaderTable = (headers: TableHeader[]) => (
    <tr>
      {headers.map((row: TableHeader, index: number) => (
        <th
          key={index}
          className={`px-6 py-3 bg-[#a8d08d] color-white min-w-[100px] border-[1px] border-[#ccc] ${row.className}`}
        >
          {row.title}
        </th>
      ))}
    </tr>
  );

  const renderInfoTable = () => (
    <table>
      <thead>{renderHeaderTable(infoTableHeaders)}</thead>
      <tbody>
        {totalPriceTableData.map((rowInfo: TotalPriceTableProps, index: number) => {
          return (
            <tr key={index} className={`border-r-[1px] border-[#ccc] ${setColor(rowInfo.type)}`}>
              <RowMeisaiTable
                dataField={rowInfo}
                key={index}
                arrayCompare={infoTableHeaders}
                renderDeleteOnEndRow={false}
                rowType={rowInfo.type}
                isFixedRow
              />
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // const renderDiscountTable = useCallback(() => { TODO:値引き機能は一旦非表示
  //   return (
  //     <table className="mb-[57px]">
  //       <thead>
  //         <tr>
  //           <th
  //             className={`px-6 py-3 bg-[#a8d08d] color-white min-w-[100px] border-[1px] border-[#ccc] ${discountTableHeaders.className}`}
  //           >
  //             {discountTableHeaders.title}
  //           </th>
  //         </tr>
  //       </thead>
  //       <tbody>
  //         {discountTableHeaders.data.map((row: DiscountTableType, index: number) => {
  //           return (
  //             <tr key={index} className={`border-r-[1px] border-[#ccc] ${rowTableColor[row.type]}`}>
  //               <td className="border-l-[1px] border-[#ccc] border-b-[1px] py-2 flex items-center">
  //                 <EstimaInput
  //                   value={discountInput[`${row.name as keyof DiscountProps}`] || ''}
  //                   className="!w-full disabled:bg-transparent bg-transparent"
  //                   onChangeInput={(value: string) => {
  //                     setDiscountInput({
  //                       ...discountInput,
  //                       [`${row.name}`]: value,
  //                     });
  //                     setMitsumoriInput((prev) => ({
  //                       ...prev,
  //                       status: mitsumoriInput.status === RecordState.NONE ? RecordState.UPDATE : mitsumoriInput.status,
  //                     }));
  //                   }}
  //                   type="number"
  //                   autoPreFix={false}
  //                   max={100}
  //                   min={1}
  //                 />
  //                 <span className="inline-block mr-2">%</span>
  //               </td>
  //             </tr>
  //           );
  //         })}
  //       </tbody>
  //     </table>
  //   );
  // }, [discountInput]);

  const onChangeRow = (table: keyof MeisaiTableRowProps, index: number, data: FilEstimaDetail) => {
    const target = [...meisaiTableRows[table]];
    target[index] = data;
    setMeisaiTableRows({
      ...meisaiTableRows,
      [table]: target,
    });
  };

  const handleDeleteRow = (table: keyof MeisaiTableRowProps, mitsumoriType: MitsumoriType, index: number) => {
    utility.confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () => {
      const target = [...meisaiTableRows[table]];
      target.splice(index, 1);
      setMeisaiTableRows({
        ...meisaiTableRows,
        [table]: target,
      });

      // 選択している行が最下行の場合は、選択行情報もクリアする
      if (target.length === selectedRow[mitsumoriType]) {
        setSelectedRow({ [mitsumoriType]: null });
      }
    });
  };

  const renderMeisaiIchiran = (tableList: TableHeader[], rowData: MeisaiTableRowProps) => (
    <table className="w-full">
      <thead>{renderHeaderTable(tableList)}</thead>
      <tbody>
        {rowData.softwareTableRows.map((row: FilEstimaDetail, index: number) => {
          return (
            <tr
              key={index}
              className={`border-r-[1px] border-[#ccc] ${selectedRow[MitsumoriType.SOFTWARE] === index && 'border-2'} ${
                index === 0 ? setColor(MitsumoriType.SOFTWARE) : ''
              }`}
              onClick={() => {
                if (selectedRow[MitsumoriType.SOFTWARE] === index) return;
                setSelectedRow({ [MitsumoriType.SOFTWARE]: index });
              }}
            >
              <RowMeisaiTable
                key={row.esd_hin_cd}
                dataField={row}
                isFixedRow={index === 0}
                rowType={MitsumoriType.SOFTWARE}
                arrayCompare={tableList}
                renderDeleteOnEndRow
                onDeleteClick={() => handleDeleteRow('softwareTableRows', MitsumoriType.SOFTWARE, index)}
                onChangeRow={(data) => onChangeRow('softwareTableRows', index, data)}
                isFreeName={row.isFreeName}
              />
            </tr>
          );
        })}
        {addNewRow(MitsumoriType.SOFTWARE)}

        {rowData.templateTableRows.map((row: FilEstimaDetail, index: number) => {
          return (
            <tr
              key={index}
              className={`border-r-[1px] border-[#ccc] ${selectedRow[MitsumoriType.TEMPLATE] === index && 'border-2'} ${
                index === 0 ? setColor(MitsumoriType.TEMPLATE) : ''
              }`}
              onClick={() => {
                if (selectedRow[MitsumoriType.TEMPLATE] === index) return;
                setSelectedRow({ [MitsumoriType.TEMPLATE]: index });
              }}
            >
              <RowMeisaiTable
                key={index}
                dataField={row}
                isFixedRow={index === 0}
                rowType={MitsumoriType.TEMPLATE}
                arrayCompare={tableList}
                renderDeleteOnEndRow
                onChangeRow={(data) => onChangeRow('templateTableRows', index, data)}
                onDeleteClick={() => handleDeleteRow('templateTableRows', MitsumoriType.TEMPLATE, index)}
                isFreeName={row.isFreeName}
              />
            </tr>
          );
        })}
        {addNewRow(MitsumoriType.TEMPLATE)}

        {rowData.licenseTableRows.map((row: FilEstimaDetail, index: number) => {
          return (
            <tr
              key={index}
              className={`border-r-[1px] border-[#ccc] ${selectedRow[MitsumoriType.LICENSE] === index && 'border-2'} ${
                index === 0 ? setColor(MitsumoriType.LICENSE) : ''
              }`}
              onClick={() => {
                if (selectedRow[MitsumoriType.LICENSE] === index) return;
                setSelectedRow({ [MitsumoriType.LICENSE]: index });
              }}
            >
              <RowMeisaiTable
                key={index}
                dataField={row}
                isFixedRow={index === 0}
                rowType={MitsumoriType.LICENSE}
                arrayCompare={tableList}
                renderDeleteOnEndRow
                onChangeRow={(data) => onChangeRow('licenseTableRows', index, data)}
                onDeleteClick={() => handleDeleteRow('licenseTableRows', MitsumoriType.LICENSE, index)}
                isFreeName={row.isFreeName}
              />
            </tr>
          );
        })}
        {addNewRow(MitsumoriType.LICENSE)}

        {rowData.usewareTableRows.map((row: FilEstimaDetail, index: number) => {
          return (
            <tr
              key={index}
              className={`border-r-[1px] border-[#ccc] ${selectedRow[MitsumoriType.USEWARE] === index && 'border-2'} ${
                index === 0 ? setColor(MitsumoriType.USEWARE) : ''
              }`}
              onClick={() => {
                if (selectedRow[MitsumoriType.USEWARE] === index) return;
                setSelectedRow({ [MitsumoriType.USEWARE]: index });
              }}
            >
              <RowMeisaiTable
                key={index}
                dataField={row}
                isFixedRow={index === 0}
                rowType={MitsumoriType.USEWARE}
                onChangeRow={(data) => onChangeRow('usewareTableRows', index, data)}
                onDeleteClick={() => handleDeleteRow('usewareTableRows', MitsumoriType.USEWARE, index)}
                arrayCompare={tableList}
                renderDeleteOnEndRow
                isFreeName={row.isFreeName}
              />
            </tr>
          );
        })}
        {addNewRow(MitsumoriType.USEWARE)}

        {rowData.discountTableRows.map((row: FilEstimaDetail, index: number) => {
          return (
            <tr
              key={index}
              className={`border-r-[1px] border-[#ccc] ${selectedRow[MitsumoriType.DISCOUNT] === index && 'border-2'}`}
              onClick={() => {
                if (selectedRow[MitsumoriType.DISCOUNT] === index) return;
                setSelectedRow({ [MitsumoriType.DISCOUNT]: index });
              }}
            >
              <RowMeisaiTable
                onChangeRow={(data) => onChangeRow('discountTableRows', index, data)}
                onDeleteClick={() => handleDeleteRow('discountTableRows', MitsumoriType.DISCOUNT, index)}
                key={index}
                dataField={row}
                rowType={MitsumoriType.DISCOUNT}
                isFixedRow={false}
                arrayCompare={tableList}
                renderDeleteOnEndRow
                isFreeName={row.isFreeName}
              />
            </tr>
          );
        })}
        {addNewRow(MitsumoriType.DISCOUNT)}
      </tbody>
    </table>
  );

  useEffect(() => {
    if (mitsumoriInput.esp_new_or_add_kb === MitsumoriKubun.REGISTER) {
      mitsumoriInput.esp_remaining_month = 0;
      mitsumoriInput.esp_soft_mainte_price = 0;
      mitsumoriInput.esp_sum_mainte_price = 0;
    } else {
      mitsumoriInput.esp_contract_period = 1; // 追加は１年固定
    }
  }, [mitsumoriInput.esp_new_or_add_kb]);

  useEffect(() => {
    calcDataTablePrice();
    // 明細を修正したときでもMitsumoriInput.statusを更新する
    if (mitsumoriInput.status === RecordState.NONE) {
      setMitsumoriInput((prev) => ({
        ...prev,
        status: RecordState.UPDATE,
      }));
    }
  }, [mitsumoriInput, discountInput, meisaiTableRows]);

  // 契約期間変更時の処理
  useEffect(() => {
    if (!mitsumoriInput.esp_contract_period || mitsumoriInput.mcp_usage_fee_discount_rate !== 0) return;
    searchContractPeriod({ variables: { input: { mcp_years: mitsumoriInput.esp_contract_period } } }).then((res) => {
      const data = res.data;
      const listMstContractPeriod = data.getMstContractPeriod;
      const contractPeriod = listMstContractPeriod[0];

      setMitsumoriInput((prev) => ({
        ...prev,
        mcp_usage_fee_discount_rate: contractPeriod.mcp_usage_fee_discount_rate || 0,
        mcp_maintenance_discount_rate: contractPeriod.mcp_maintenance_discount_rate || 0,
      }));
    });
  }, [mitsumoriInput.esp_contract_period]);

  // 一次店コード変更時の処理
  useEffect(() => {
    if (!mitsumoriInput.esp_primary_cd) return;

    searchMstPrimarySales({ variables: { input: { mps_primary_cd: mitsumoriInput.esp_primary_cd } } }).then((res) => {
      setMitsumoriInput((prev) => ({
        ...prev,
        mps_primary_nm: res.data.getMstPrimarySales[0].mps_primary_nm,
      }));
    });
  }, [mitsumoriInput.esp_primary_cd]);

  // 二次店コード変更時の処理
  useEffect(() => {
    if (!mitsumoriInput.esp_secondary_cd) return;

    searchMstSecondarySales({
      variables: {
        input: { mss_primary_cd: mitsumoriInput.esp_primary_cd, mss_secondary_cd: mitsumoriInput.esp_secondary_cd },
      },
    }).then((res) => {
      setMitsumoriInput((prev) => ({
        ...prev,
        mss_secondary_nm: res.data.getMstSecondarySales[0].mss_secondary_nm,
      }));
    });
  }, [mitsumoriInput.esp_secondary_cd]);

  // 拠点コード変更時の処理
  useEffect(() => {
    if (!mitsumoriInput.esp_office_cd) return;

    searchMstOffice({ variables: { input: { mo_office_cd: mitsumoriInput.esp_office_cd } } }).then((res) => {
      setMitsumoriInput((prev) => ({
        ...prev,
        ...res.data.getMstOffice[0],
      }));
    });
  }, [mitsumoriInput.esp_office_cd]);

  // 一次店、二次店、製品区分のどれかが変更されたときの処理
  useEffect(() => {
    fetchMstNetRate({
      variables: {
        input: {
          mnr_product_cd: mitsumoriInput.esp_product_cd,
          mnr_primary_cd: mitsumoriInput.esp_primary_cd,
          mnr_secondary_cd: mitsumoriInput.esp_secondary_cd,
        },
      },
    }).then((res) => {
      const { getMstNetRate: netRateData } = res.data;
      setMitsumoriInput((prev) => ({
        ...prev,
        mnr_software_net_rate: netRateData[0]?.mnr_software_net_rate,
        mnr_template_net_rate: netRateData[0]?.mnr_template_net_rate,
        mnr_license_net_rate: netRateData[0]?.mnr_license_net_rate,
        mnr_useware_net_rate: netRateData[0]?.mnr_useware_net_rate,
        mnr_maintenance_net_rate: netRateData[0]?.mnr_maintenance_net_rate,
      }));
    });
  }, [mitsumoriInput.esp_product_cd, mitsumoriInput.esp_primary_cd, mitsumoriInput.esp_secondary_cd]);

  // 製品区分が変更されたときの処理
  useEffect(() => {
    if (!mitsumoriInput.esp_product_cd || mitsumoriInput.mpr_maintenance_limit) return;
    searchMstProduct({
      variables: {
        input: {
          mpr_product_cd: mitsumoriInput.esp_product_cd,
        },
      },
    }).then((res) => {
      setMitsumoriInput((prev) => ({
        ...prev,
        ...res.data.getMstProduct[0],
      }));
    });
  }, [mitsumoriInput.esp_product_cd]);

  useEffect(() => {
    searchLogMaintenance();
    fetchMstHinmei();
  }, []);

  // 初期データ表示
  useEffect(() => {
    // ユーザー情報が取得できたら、初期表示処理に進む。
    // 実際に影響があるのは新規データのときのみだが、正しいデータ状態で進むという意味で
    // ここでチェックを行う
    if (!user) return;

    // 見積一覧から見積番号（親）が送られたかどうかのみで判断できる
    if (location.state?.no) {
      initEditData(location.state.no, location.state.kb_no);
    } else {
      initNewData();
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    getReportFiles();
  }, [user]);

  if (logMData?.getLogMaintenance.length > 0 || subInsLogData) return <Maintenance />;

  return (
    <MainLayout isLoading={isUserLoading || isProcessing}>
      {renderInputArea()}
      <div className="my-0 mx-auto max-w-[84%]">
        <div className="flex justify-between items-end">
          {renderInfoTable()}

          <div>
            <button onClick={handleRegister} className="py-4 px-8 bg-green-400 mr-2 rounded-[3px] text-white">
              登録
            </button>
            <button onClick={handleExportPdf} className="py-4 px-8 bg-black rounded-[3px] text-white">
              PDF出力
            </button>
          </div>
        </div>
        <br />
        <br />
        {renderMeisaiIchiran(meisaiIchiranHeader, meisaiTableRows)}
      </div>
      <Dialog
        open={isShowGuideOffice}
        onClose={() => setIsShowGuideOffice(false)}
        className="relative z-50 w-full h-full"
      >
        <div className="fixed w-full h-full inset-0 flex items-center justify-center">
          <ModalGuideOffice setIsOpen={setIsShowGuideOffice} onCallbackFunction={handleSelectGuideOffice} />
        </div>
      </Dialog>
      <Dialog open={isShowGuideUser} onClose={() => setIsShowGuideUser(false)} className="relative z-50 w-full h-full">
        <div className="fixed w-full h-full inset-0 flex items-center justify-center">
          <ModalGuideUser setIsOpen={setIsShowGuideUser} onCallbackFunction={handleSelectGuideUser} />
        </div>
      </Dialog>
      <Dialog
        open={isShowPrimaryStore}
        onClose={() => setIsShowPrimaryStore(false)}
        className="relative z-50 w-full h-full"
      >
        <div className="fixed w-full h-full inset-0 flex items-center justify-center">
          <ModalGuidePrimaryStore setIsOpen={setIsShowPrimaryStore} onCallbackFunction={handleSelectPrimaryStore} />
        </div>
      </Dialog>
      <Dialog
        open={isShowSecondaryStore}
        onClose={() => setIsShowSecondaryStore(false)}
        className="relative z-50 w-full h-full"
      >
        <div className="fixed w-full h-full inset-0 flex items-center justify-center">
          <ModalGuideSecondaryStore
            setIsOpen={setIsShowSecondaryStore}
            onCallbackFunction={handleSelectSecondaryStore}
            primaryStore={{
              mps_primary_cd: mitsumoriInput.esp_primary_cd ? mitsumoriInput.esp_primary_cd.toString() : '',
              mps_primary_nm: '',
              error: {
                isErrorPrimaryCd: false,
              },
            }}
          />
        </div>
      </Dialog>
      <ModalGuideProduct
        open={isOpenProductDivison}
        onClose={() => setIsOpenProductDivison(false)}
        onClickRow={handleSelectProductDivision}
      />
      <Dialog
        open={isShowGuideContractPeriod}
        onClose={() => setIsShowGuideContractPeriod(false)}
        className="relative z-50 w-full h-full"
      >
        <div className="fixed w-full h-full inset-0 flex items-center justify-center">
          <ModalGuideContractPeriod
            setIsOpen={setIsShowGuideContractPeriod}
            onCallbackFunction={handleSelectContractPeriod}
          />
        </div>
      </Dialog>
      <Dialog
        open={isShowModalRemarks}
        onClose={() => setIsShowModalRemarks(false)}
        className="relative z-50 w-full h-full"
      >
        <div className="fixed w-full h-full inset-0 flex items-center justify-center">
          <ModalGuideRemarks
            mstRemarksKbValue={mstRemarksKb}
            setIsOpen={setIsShowModalRemarks}
            onCallbackFunction={handleSelectRemarks}
          />
        </div>
      </Dialog>
      <Dialog
        open={isShowModaGuideHinmei}
        onClose={() => setIsShowModaGuideHinmei(false)}
        className="relative z-50 w-full h-full"
      >
        <div className="fixed w-full h-full inset-0 flex items-center justify-center">
          <ModalGuideHinmei
            setIsOpen={setIsShowModaGuideHinmei}
            primaryCd={mitsumoriInput.esp_primary_cd ?? 0}
            secondaryCd={mitsumoriInput.esp_secondary_cd ?? 0}
            productCd={mitsumoriInput.esp_product_cd ?? 0}
            contractPeriod={
              mitsumoriInput.mpr_discount_type === DiscountType.MAINTAIN_PRICE
                ? 0
                : (mitsumoriInput.esp_contract_period ?? 0)
            }
            hinmeiKb={hinmeiKbSelected}
            onCallbackFunction={handleAddFreeInputRow}
          />
        </div>
      </Dialog>
    </MainLayout>
  );
};

export default QuotationInput;
